import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { FACT_FINDS } from './factFinds'
import { FactFindSection } from './FactFindSection'
import { CoverConfirmedModal, CoversTab } from './CoverConfirmed'
import { QuotePackModal } from './QuotePackModal'
import { Dashboard } from './Dashboard'
import { Leads } from './Leads'
import { ClientDetailModal } from './ClientDetailModal'
import { Complaints } from './Complaints'
import { Claims } from './Claims'
import { Meetings } from './Meetings'
import { FSO } from './fso'
import { Payments } from './Payments'
import { ClientBadges } from './ClientBadges'
import { StatusReasonModal } from './StatusReasonModal'
import { SmsComposeModal } from './SmsComposeModal'
import { useNavigate, useLocation } from 'react-router-dom'
import { InstantSearch } from './InstantSearch'


const SUPABASE_STORAGE = 'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets'
const LOGO_URL = `${SUPABASE_STORAGE}/BIB-Light-%28Horizontal%29%20%281%29.png`
const ICON_URL = `${SUPABASE_STORAGE}/BIB-Dark-(Horizontal).png`

const BROKER_PHOTOS = {
  'Jesse Roach': `${SUPABASE_STORAGE}/DSC_0061_VSCO.jpg`,
  'Ben Rogers': `${SUPABASE_STORAGE}/DSC_0013_VSCO.jpg`,
  'Mitch Speer': `${SUPABASE_STORAGE}/DSC_0023_VSCO.jpg`,
}
const BROKER_TITLES = {
  'Ben Rogers': 'Principal',
  'Jesse Roach': 'Co-Founder',
  'Mitch Speer': 'Director',
}

const LEAD_STATUSES = ['New Lead','In Progress','Quote Sent','Awaiting Client','Under Referral','No Contact 1','No Contact 2','No Contact 3','Policy Placed','Not Interested']
const REASON_REQUIRED_STATUSES = ['Not Interested']
const STATUS_COLORS = {
  'New Lead':'#1e9e75','In Progress':'#e6a817','Quote Sent':'#3b82f6','Awaiting Client':'#e6a817','Under Referral':'#8b5cf6',
  'No Contact 1':'#f97316','No Contact 2':'#ea580c','No Contact 3':'#c2410c','Policy Placed':'#1e3d30','Not Interested':'#dc3545'
}
const PRIORITY_COLORS = { High:'#dc3545', Medium:'#e6a817', Low:'#1e3d30' }
const BROKERS = ['Ben Rogers','Jesse Roach','Mitch Speer']
const FACT_FIND_OPTIONS = Object.keys(FACT_FINDS)
const GEOAPIFY_KEY = 'a8f19b87ffe24f89b222eda754c1407a'
const LEAD_SOURCES = ['Direct Enquiry','Referral Partner','Financial Services Online','QuoteRack','Website','Google','Social Media','BNI','Door Knocking','Family / Friend','Existing Client','Tower Life','Other']

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'numeric'}) }
// McLardy retains 40% of insurer commission (verified from remittance). Referrer share is calculated on the 60% AR pool, commission only — never fee.
const MCLARDY_COMM_RETENTION = 0.40
const DEFAULT_REFERRAL_RATE = 0.40
const getReferralRate = (partnerName, partners) => {
  const p = (partners || []).find(x => x.partner_name === partnerName)
  const pct = Number(p?.referral_rate)
  return Number.isFinite(pct) && pct > 0 ? pct / 100 : DEFAULT_REFERRAL_RATE
}
const daysUntil = (d) => { if (!d) return null; return Math.ceil((new Date(d)-new Date())/86400000) }
const getInitials = (name) => { if (!name) return '?'; return name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() }
const normaliseAuPhone = (phone) => { const raw=(phone||'').replace(/\s+/g,'').trim(); if(!raw) return ''; if(raw.startsWith('+')) return raw; if(raw.startsWith('61')) return `+${raw}`; if(raw.startsWith('0')) return `+61${raw.slice(1)}`; return raw }
const makeAuditEntry = (type, action, broker, changes=[]) => ({ type, action, broker, timestamp: new Date().toISOString(), changes })
const makeActivityEntry = (type, text, icon='📝') => ({ type, text, timestamp: new Date().toISOString(), icon })

const openHtmlInWindow = (html, title) => {
  const w = window.open('', '_blank', 'width=1100,height=900')
  if (!w) return
  w.document.write(`<html><head><title>${title || 'Benchmark'}</title><style>
    *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
    body{margin:0;background:#f7faf8;font-family:'Helvetica Neue',Arial,sans-serif;}
    .pdf-bar{position:fixed;top:16px;right:16px;z-index:9999;display:flex;gap:8px}
    .pdf-btn{background:#1e3d30;color:#fff;border:none;padding:10px 20px;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer;}
    .pdf-btn.close-btn{background:#6b8278}
    @media print{.pdf-bar{display:none!important}@page{margin:0;size:A4}}
  </style></head><body><div class="pdf-bar"><button class="pdf-btn" onclick="window.print()">🖨️ Save as PDF</button><button class="pdf-btn close-btn" onclick="window.close()">✕ Close</button></div>${html}</body></html>`)
  w.document.close(); w.focus()
}

// ─── PUSH NOTIFICATIONS ───────────────────────────────────────────────────────
async function requestPushPermission() {
  if (!('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  const result = await Notification.requestPermission()
  return result === 'granted'
}

function sendPushNotification(title, body, onClick) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  const n = new Notification(title, {
    body,
    icon: ICON_URL,
    tag: 'benchmark-sms',
    renotify: true,
  })
  if (onClick) n.onclick = () => { window.focus(); onClick(); n.close() }
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
:root{
  --forest:#1e3d30;--forest-dark:#162d23;--forest-light:#2a5240;
  --forest-subtle:rgba(30,61,48,0.08);--forest-border:rgba(30,61,48,0.15);
  --orange:#f26522;--orange-light:rgba(242,101,34,0.12);
  --bg:#f0f4f2;--surface:#ffffff;--surface2:#f5f7f6;
  --border:#e2e8e5;--text:#1a2e25;--muted:#6b8278;
  --danger:#dc3545;--warning:#e6a817;--info:#3b82f6;--purple:#8b5cf6;
  --font:'DM Sans',sans-serif;
}
body{font-family:var(--font);background:var(--bg);color:var(--text);min-height:100vh;font-size:14px}
button{font-family:var(--font);cursor:pointer}
input,select,textarea{font-family:var(--font)}
.login-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:var(--forest-dark);position:relative;overflow:hidden}
.login-card{display:flex;width:840px;min-height:500px;border-radius:20px;overflow:hidden;box-shadow:0 40px 80px rgba(0,0,0,0.4)}
.login-left{background:var(--forest);flex:1;padding:48px;display:flex;flex-direction:column;justify-content:space-between}
.logo-box{width:44px;height:44px;background:#fff;border-radius:10px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:20px;color:var(--forest)}
.login-hero h1{font-size:30px;font-weight:600;color:#fff;line-height:1.3;margin-bottom:12px}
.login-hero h1 span{color:var(--orange)}
.login-hero p{color:rgba(255,255,255,0.5);font-size:13px;line-height:1.7}
.login-stats{display:flex;gap:28px}
.login-stat strong{display:block;color:#fff;font-size:20px;font-weight:600}
.login-stat span{color:rgba(255,255,255,0.5);font-size:12px}
.login-right{background:var(--surface);width:360px;padding:44px;display:flex;flex-direction:column;justify-content:center}
.login-title{font-size:22px;font-weight:600;margin-bottom:6px}
.login-sub{color:var(--muted);font-size:13px;margin-bottom:28px}
.error-msg{background:rgba(220,53,69,.08);border:1px solid rgba(220,53,69,.2);color:var(--danger);padding:10px 14px;border-radius:8px;font-size:13px;margin-bottom:16px}
.form-group{margin-bottom:16px}
.form-label{display:block;font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px}
.form-input{width:100%;background:var(--surface2);border:1.5px solid var(--border);border-radius:10px;padding:10px 14px;color:var(--text);font-size:14px;outline:none;transition:border-color .2s}
.form-input:focus{border-color:var(--forest)}
.form-select{width:100%;background:var(--surface2);border:1.5px solid var(--border);border-radius:10px;padding:10px 14px;color:var(--text);font-size:14px;outline:none}
.form-select:focus{border-color:var(--forest)}
textarea.form-input{resize:vertical;min-height:80px}
.btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:10px 18px;border-radius:10px;font-size:14px;font-weight:500;border:none;transition:all .2s}
.btn-primary{background:var(--forest);color:#fff}
.btn-primary:hover{background:var(--forest-light)}
.btn-primary:disabled{opacity:.6;cursor:not-allowed}
.btn-secondary{background:var(--surface2);color:var(--text);border:1.5px solid var(--border)}
.btn-secondary:hover{border-color:var(--forest);color:var(--forest)}
.btn-secondary:disabled{opacity:.5;cursor:not-allowed}
.btn-orange{background:var(--orange);color:#fff}
.btn-orange:hover{opacity:.9}
.btn-danger{background:rgba(220,53,69,.1);color:var(--danger);border:1px solid rgba(220,53,69,.2)}
.btn-danger:hover{background:rgba(220,53,69,.18)}
.btn-sm{padding:6px 14px;font-size:12px;border-radius:8px}
.btn-full{width:100%}
.app{display:flex;min-height:100vh}
.sidebar{width:260px;background:var(--forest-dark);display:flex;flex-direction:column;flex-shrink:0;position:fixed;top:0;left:0;height:100vh;z-index:100;overflow-y:auto}
.sidebar-logo{padding:20px 18px;border-bottom:1px solid rgba(255,255,255,0.08);display:flex;align-items:center;gap:12px;background:rgba(0,0,0,0.15)}
.sidebar-nav{flex:1;padding:10px 8px}
.nav-section{font-size:9px;font-weight:700;color:rgba(255,255,255,0.3);text-transform:uppercase;letter-spacing:1.2px;padding:14px 12px 5px}
.nav-item{display:flex;align-items:center;gap:11px;padding:9px 12px;border-radius:9px;color:rgba(255,255,255,0.78);font-size:13.5px;font-weight:600;transition:all .15s;margin-bottom:1px;border:none;background:none;width:100%;text-align:left;cursor:pointer;letter-spacing:-.1px}
.nav-item:hover{background:rgba(255,255,255,0.08);color:#fff;transform:translateX(2px)}
.nav-item.active{background:linear-gradient(135deg,rgba(242,101,34,0.25),rgba(242,101,34,0.15));color:#fff;border-left:3px solid var(--orange);padding-left:9px;box-shadow:0 2px 8px rgba(0,0,0,0.15)}
.nav-badge{margin-left:auto;background:var(--orange);color:#fff;font-size:10px;font-weight:700;padding:1px 6px;border-radius:10px;min-width:18px;text-align:center}
.nav-badge-warn{background:var(--warning);color:#000}
.nav-badge-green{background:var(--forest);color:#fff}
.sidebar-footer{padding:12px 10px;border-top:1px solid rgba(255,255,255,0.08);background:rgba(0,0,0,0.1)}
.user-chip{display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:10px;background:rgba(255,255,255,0.04)}
.avatar{width:56px;height:56px;border-radius:50%;background:var(--orange);display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#fff;flex-shrink:0}
.user-name{color:#fff;font-size:16px;font-weight:700;letter-spacing:-.2px}
.user-role{color:rgba(255,255,255,0.4);font-size:12px;margin-top:2px}
.logout-btn{background:none;border:none;color:rgba(255,255,255,0.3);font-size:11px;padding:4px 10px;cursor:pointer;width:100%;text-align:left}
.logout-btn:hover{color:rgba(255,255,255,0.6)}
.main{margin-left:260px;flex:1;display:flex;flex-direction:column;min-height:100vh}
.topbar{height:56px;background:var(--surface);border-bottom:1.5px solid var(--border);display:flex;align-items:center;justify-content:space-between;padding:0 24px;position:sticky;top:0;z-index:50}
.page-title{font-size:16px;font-weight:600}
.content{padding:24px;flex:1}
.stats-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:24px}
.stat-card{background:var(--surface);border:1.5px solid var(--border);border-radius:14px;padding:20px;position:relative;overflow:hidden}
.stat-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;border-radius:14px 14px 0 0}
.sc-green::before{background:var(--forest)}.sc-orange::before{background:var(--orange)}.sc-warn::before{background:var(--warning)}.sc-blue::before{background:var(--info)}
.stat-label{font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px}
.stat-value{font-size:28px;font-weight:600;margin-bottom:4px}
.stat-sub{font-size:12px;color:var(--muted)}
.c-forest{color:var(--forest)}.c-orange{color:var(--orange)}.c-warn{color:var(--warning)}.c-blue{color:var(--info)}
.card{background:var(--surface);border:1.5px solid var(--border);border-radius:14px;overflow:hidden}
.card-header{padding:16px 20px;border-bottom:1.5px solid var(--border);display:flex;align-items:center;justify-content:space-between}
.card-title{font-size:14px;font-weight:600}
.tbl-header{display:grid;gap:12px;padding:10px 20px;border-bottom:1.5px solid var(--border);background:var(--surface2)}
.tbl-header span{font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.4px}
.tbl-row{display:grid;gap:12px;padding:14px 20px;border-bottom:1.5px solid var(--border);align-items:center;cursor:pointer;transition:background .15s}
.tbl-row:last-child{border-bottom:none}
.tbl-row:hover{background:var(--surface2)}
.cell-main{display:flex;align-items:center;gap:10px}
.cell-av{width:32px;height:32px;border-radius:8px;background:var(--forest-subtle);border:1.5px solid var(--forest-border);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:var(--forest);flex-shrink:0}
.badge{display:inline-flex;align-items:center;padding:2px 7px;border-radius:20px;font-size:10px;font-weight:600;white-space:nowrap}
.badge-green{background:var(--forest-subtle);color:var(--forest)}
.badge-orange{background:var(--orange-light);color:var(--orange)}
.badge-red{background:rgba(220,53,69,0.1);color:var(--danger)}
.badge-warn{background:rgba(230,168,23,0.12);color:#7a5500}
.badge-purple{background:rgba(139,92,246,0.12);color:var(--purple)}
.badge-blue{background:rgba(59,130,246,0.12);color:var(--info)}
.badge-grey{background:var(--surface2);color:var(--muted);border:1px solid var(--border)}
.task-item{display:flex;align-items:flex-start;gap:12px;padding:14px 20px;border-bottom:1.5px solid var(--border);transition:background .15s}
.task-item:last-child{border-bottom:none}
.task-item:hover{background:var(--surface2)}
.task-check{width:18px;height:18px;border-radius:50%;border:2px solid var(--border);cursor:pointer;flex-shrink:0;margin-top:2px;transition:all .15s;display:flex;align-items:center;justify-content:center;background:#fff}
.task-check:hover{border-color:var(--forest)}
.task-check.done{background:var(--forest);border-color:var(--forest)}
.task-body{flex:1}
.task-text{font-size:13px;font-weight:500;line-height:1.4}
.task-text.done{text-decoration:line-through;color:var(--muted)}
.task-meta{font-size:11px;color:var(--muted);margin-top:2px}
.priority-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;margin-top:5px}
.ren-item{display:flex;align-items:center;padding:14px 20px;border-bottom:1.5px solid var(--border);gap:16px}
.ren-item:last-child{border-bottom:none}
.days-pill{font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;white-space:nowrap}
.d-red{background:rgba(220,53,69,.1);color:var(--danger)}
.d-amber{background:rgba(230,168,23,.1);color:var(--warning)}
.d-green{background:rgba(30,61,48,.1);color:var(--forest)}
.overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:200;display:flex;align-items:center;justify-content:center;padding:24px;backdrop-filter:blur(3px)}
.modal{background:var(--surface);border:1.5px solid var(--border);border-radius:18px;width:100%;max-width:700px;max-height:92vh;overflow-y:auto;box-shadow:0 24px 60px rgba(0,0,0,.15)}
.modal-head{padding:20px 24px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;background:var(--surface);z-index:1;border-bottom:1px solid var(--border)}
.modal-title{font-size:16px;font-weight:600}
.modal-close{background:var(--surface2);border:1px solid var(--border);color:var(--muted);width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:16px}
.modal-close:hover{color:var(--text)}
.modal-body{padding:20px 24px 24px}
.form-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.form-grid .full{grid-column:1/-1}
.form-actions{display:flex;gap:8px;justify-content:flex-end;margin-top:20px;padding-top:16px;border-top:1px solid var(--border)}
.section-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px}
.section-title{font-size:15px;font-weight:600}
.section-sub{font-size:12px;color:var(--muted);margin-top:2px}
.loading{display:flex;align-items:center;justify-content:center;height:200px;color:var(--muted);font-size:13px;gap:8px}
.spinner{width:16px;height:16px;border:2px solid var(--border);border-top-color:var(--forest);border-radius:50%;animation:spin .8s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.empty{text-align:center;padding:48px 24px;color:var(--muted)}
.empty-icon{font-size:32px;margin-bottom:12px}
.empty-title{font-size:15px;font-weight:600;color:var(--text);margin-bottom:6px}
.empty-sub{font-size:13px}
.toast{position:fixed;bottom:24px;right:24px;background:var(--forest);color:#fff;padding:12px 20px;border-radius:12px;font-size:13px;font-weight:500;z-index:999;border-left:4px solid var(--orange);box-shadow:0 8px 24px rgba(0,0,0,.2);animation:slideUp .3s ease}
@keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
.report-row{display:flex;align-items:center;padding:14px 20px;border-bottom:1.5px solid var(--border);gap:12px}
.report-row:last-child{border-bottom:none}
.report-bar-wrap{flex:1;height:6px;background:var(--surface2);border-radius:3px;overflow:hidden;margin:0 12px}
.report-bar{height:100%;background:var(--forest);border-radius:3px;transition:width .8s ease}
.ff-field{display:flex;flex-direction:column;gap:4px}
.ff-field.full{grid-column:1/-1}
.ff-label{font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.5px}
.ff-input{background:#fff;border:1.5px solid #d8e2dd;border-radius:8px;padding:10px 12px;color:#1a2e25;font-size:13px;outline:none;transition:border-color .2s;font-family:var(--font);width:100%}
.ff-input:focus{border-color:var(--forest)}
textarea.ff-input{resize:vertical;min-height:60px}
.ff-radio-group{display:flex;gap:8px;flex-wrap:wrap}
.ff-radio-label{display:flex;align-items:center;gap:5px;font-size:12px;cursor:pointer;background:var(--surface);padding:5px 10px;border-radius:7px;border:1.5px solid var(--border);transition:all .15s;user-select:none}
.ff-radio-label:hover{border-color:var(--forest)}
.ff-radio-label input{accent-color:var(--forest)}
.ff-checkbox-group{display:flex;flex-wrap:wrap;gap:6px}
.ff-checkbox-label{display:flex;align-items:center;gap:5px;font-size:12px;cursor:pointer;background:var(--surface);padding:5px 10px;border-radius:7px;border:1.5px solid var(--border);transition:all .15s;user-select:none}
.ff-checkbox-label input{accent-color:var(--forest)}
.client-strip{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px}
.strip-item{background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:10px 14px}
.strip-label{font-size:10px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.4px;margin-bottom:3px}
.strip-value{font-size:13px;font-weight:600;color:var(--text)}
.sms-modal{background:var(--surface);border:1.5px solid var(--border);border-radius:18px;width:100%;max-width:500px;box-shadow:0 24px 60px rgba(0,0,0,.2);overflow:hidden}
.sms-modal-head{padding:16px 20px;background:linear-gradient(135deg,var(--forest),var(--forest-dark));display:flex;align-items:center;justify-content:space-between}
.sms-modal-title{font-size:14px;font-weight:600;color:#fff}
.sms-modal-sub{font-size:11px;color:rgba(255,255,255,0.5);margin-top:2px}
.cpd-broker-card{background:var(--surface);border:1.5px solid var(--border);border-radius:14px;padding:20px;position:relative;overflow:hidden}
.cpd-broker-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:var(--forest);border-radius:14px 14px 0 0}
.cpd-progress-bar{height:6px;background:var(--border);border-radius:3px;overflow:hidden;margin-top:8px}
.cpd-progress-fill{height:100%;border-radius:3px;background:linear-gradient(90deg,var(--forest),var(--orange));transition:width .8s ease}
.cpd-entry-row{display:grid;grid-template-columns:2fr 1fr 1fr 1fr 60px;gap:12px;padding:12px 20px;border-bottom:1.5px solid var(--border);align-items:center;font-size:12px}
.cpd-entry-row:last-child{border-bottom:none}
.comp-blurb{background:linear-gradient(135deg,rgba(220,53,69,0.06),rgba(230,168,23,0.06));border:1.5px solid rgba(220,53,69,0.2);border-radius:14px;padding:16px 20px;margin-bottom:20px}
.comp-blurb-title{font-size:12px;font-weight:700;color:var(--danger);text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px;display:flex;align-items:center;gap:6px}
.comp-blurb-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.comp-blurb-item{background:#fff;border-radius:9px;padding:10px 13px;border:1px solid rgba(220,53,69,0.15)}
.comp-blurb-item-title{font-size:11px;font-weight:700;color:var(--danger);margin-bottom:3px}
.comp-blurb-item-text{font-size:11px;color:var(--text);line-height:1.6}
.comp-tabs{display:flex;gap:4px;margin-bottom:16px;border-bottom:1.5px solid var(--border)}
.comp-tab{padding:8px 18px;background:none;border:none;border-bottom:2px solid transparent;font-weight:500;color:var(--muted);font-size:13px;cursor:pointer;margin-bottom:-1.5px;font-family:var(--font);transition:all .15s}
.comp-tab.active{border-bottom-color:var(--danger);color:var(--danger);font-weight:600}
.comp-tab:hover{color:var(--text)}
.days-counter{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700}
.dc-green{background:rgba(30,61,48,0.1);color:var(--forest)}
.dc-amber{background:rgba(230,168,23,0.15);color:#7a5500}
.dc-red{background:rgba(220,53,69,0.12);color:var(--danger)}
.comp-card{background:var(--surface);border:1.5px solid var(--border);border-radius:12px;padding:16px 20px;margin-bottom:10px;cursor:pointer;transition:all .15s}
.comp-card:hover{border-color:rgba(220,53,69,0.4);background:rgba(220,53,69,0.02)}
.comp-card.selected{border-color:var(--danger);background:rgba(220,53,69,0.03)}
.comp-status{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:600}
.cs-open{background:rgba(59,130,246,0.12);color:#2563eb}
.cs-inprogress{background:rgba(230,168,23,0.12);color:#7a5500}
.cs-resolved{background:rgba(30,61,48,0.1);color:var(--forest)}
.cs-afca{background:rgba(220,53,69,0.12);color:var(--danger)}
.vuln-badge{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700;background:rgba(139,92,246,0.12);color:#7c3aed}
.comp-form-section{background:var(--surface2);border:1.5px solid var(--border);border-radius:12px;padding:16px 20px;margin-bottom:14px}
.comp-form-section-title{font-size:12px;font-weight:700;color:var(--forest);text-transform:uppercase;letter-spacing:.5px;margin-bottom:14px;display:flex;align-items:center;gap:6px}
.reg-row{display:grid;grid-template-columns:1.5fr 1fr 1fr 1fr 1fr 1fr;gap:10px;padding:12px 16px;border-bottom:1.5px solid var(--border);align-items:center;font-size:12px}
.reg-row:last-child{border-bottom:none}
.reg-header{background:var(--surface2);font-weight:600;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.4px}
.stat-mini{background:var(--surface);border:1.5px solid var(--border);border-radius:10px;padding:14px 16px}
.stat-mini-label{font-size:10px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px}
.stat-mini-value{font-size:22px;font-weight:700}
.mfa-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:var(--forest-dark)}
.mfa-card{background:var(--surface);border-radius:20px;padding:48px;width:420px;box-shadow:0 40px 80px rgba(0,0,0,0.4)}
.mfa-title{font-size:22px;font-weight:600;margin-bottom:8px}
.mfa-sub{color:var(--muted);font-size:13px;margin-bottom:28px;line-height:1.6}
.mfa-step{background:var(--surface2);border:1.5px solid var(--border);border-radius:12px;padding:16px;margin-bottom:16px}
.mfa-step-num{width:24px;height:24px;border-radius:50%;background:var(--forest);color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;margin-bottom:8px}
.mfa-step-title{font-size:13px;font-weight:600;margin-bottom:4px}
.mfa-step-sub{font-size:12px;color:var(--muted)}
.qr-wrap{display:flex;justify-content:center;margin:16px 0;padding:16px;background:white;border-radius:12px;border:1.5px solid var(--border)}
.broker-avatar{width:72px;height:72px;border-radius:50%;object-fit:cover;object-position:center;flex-shrink:0;border:2px solid rgba(255,255,255,0.2)}
.timeline{padding:0 24px 24px}
.timeline-item{display:flex;gap:12px;margin-bottom:16px;position:relative}
.timeline-item::before{content:'';position:absolute;left:15px;top:28px;bottom:-8px;width:2px;background:var(--border)}
.timeline-item:last-child::before{display:none}
.timeline-dot{width:32px;height:32px;border-radius:50%;background:var(--surface2);border:2px solid var(--border);display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0;z-index:1}
.timeline-content{flex:1;padding-top:4px}
.timeline-text{font-size:13px;font-weight:500;line-height:1.4}
.timeline-time{font-size:11px;color:var(--muted);margin-top:2px}
.quick-bar{background:var(--surface);border-bottom:1.5px solid var(--border);padding:10px 24px;display:flex;gap:8px;align-items:center;position:sticky;top:56px;z-index:40}
.quick-btn{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border-radius:8px;font-size:12px;font-weight:600;border:1.5px solid var(--border);background:var(--surface2);color:var(--text);cursor:pointer;transition:all .15s;white-space:nowrap;font-family:var(--font)}
.quick-btn:hover{border-color:var(--forest);color:var(--forest);background:var(--forest-subtle)}
.quick-btn.orange{border-color:rgba(242,101,34,0.3);color:var(--orange);background:rgba(242,101,34,0.06)}
.quick-btn.orange:hover{border-color:var(--orange);background:rgba(242,101,34,0.12)}
.quick-btn:disabled{opacity:.4;cursor:not-allowed}
.reason-modal{background:var(--surface);border:1.5px solid var(--border);border-radius:16px;width:100%;max-width:440px;overflow:hidden;box-shadow:0 24px 60px rgba(0,0,0,.2)}
.reason-preset{display:block;width:100%;text-align:left;padding:9px 14px;background:var(--surface2);border:1.5px solid var(--border);border-radius:8px;font-size:13px;cursor:pointer;margin-bottom:6px;font-family:var(--font);color:var(--text);transition:all .15s}
.reason-preset:hover{border-color:var(--forest);background:var(--forest-subtle);color:var(--forest)}
.reason-preset.selected{border-color:var(--forest);background:var(--forest-subtle);color:var(--forest);font-weight:600}
.checklist-wrap{background:linear-gradient(135deg,rgba(30,61,48,0.05),rgba(242,101,34,0.04));border:1.5px solid var(--forest-border);border-radius:14px;padding:16px 20px;margin-bottom:20px}
.checklist-title-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.checklist-title{font-size:12px;font-weight:700;color:var(--forest);text-transform:uppercase;letter-spacing:.5px}
.checklist-items{display:grid;grid-template-columns:repeat(5,1fr);gap:8px}
.checklist-item{display:flex;flex-direction:column;align-items:center;gap:5px;padding:10px 6px;border-radius:10px;border:1.5px solid var(--border);background:var(--surface);cursor:pointer;transition:all .2s;user-select:none;text-align:center}
.checklist-item:hover{border-color:var(--forest);background:var(--forest-subtle)}
.checklist-item.checked{border-color:var(--forest);background:var(--forest-subtle)}
.checklist-item-icon{font-size:20px}
.checklist-item-label{font-size:10px;font-weight:600;color:var(--muted);line-height:1.3}
.checklist-item.checked .checklist-item-label{color:var(--forest)}
.checklist-pips{display:flex;gap:4px;margin-top:10px}
.checklist-pip{flex:1;height:4px;border-radius:2px;background:var(--border);transition:background .3s}
.checklist-pip.filled{background:var(--forest)}
.activity-item{display:flex;align-items:flex-start;gap:10px;padding:12px 20px;border-bottom:1px solid var(--border);cursor:pointer;transition:background .15s}
.activity-item:last-child{border-bottom:none}
.activity-item:hover{background:var(--surface2)}
.activity-dot{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0;margin-top:1px}
@media print{
  .sidebar,.topbar,.btn,.modal-close,.form-actions,.comp-tabs,.toast,.quick-bar{display:none !important}
  .main{margin-left:0 !important}
  body{background:#fff !important}
}
`

// ─── ADDRESS AUTOCOMPLETE ─────────────────────────────────────────────────────
function AddressInput({ value, onChange, placeholder, className }) {
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [searching, setSearching] = useState(false)
  const timerRef = useRef(null)

  const handleChange = (e) => {
    const val = e.target.value; onChange(val); setShowSuggestions(false); setSuggestions([])
    if (timerRef.current) clearTimeout(timerRef.current)
    if (val.length < 3) return
    timerRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const res = await fetch(`https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(val)}&country=au&limit=6&apiKey=${GEOAPIFY_KEY}`)
        const data = await res.json()
        if (data.features?.length) { setSuggestions(data.features); setShowSuggestions(true) }
      } catch(e) {}
      setSearching(false)
    }, 350)
  }

  const selectSuggestion = (feature) => {
    const p = feature.properties
    const parts = [p.housenumber && p.street ? `${p.housenumber} ${p.street}` : p.street||p.name, p.suburb||p.city, p.state_code||p.state, p.postcode].filter(Boolean)
    onChange(parts.join(', ')); setSuggestions([]); setShowSuggestions(false)
  }

  return (
    <div style={{position:'relative'}}>
      <input className={className||'form-input'} type="text" value={value} placeholder={placeholder||'Start typing address...'} onChange={handleChange} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} autoComplete="off" />
      {searching && <div style={{fontSize:11,color:'var(--muted)',marginTop:3,paddingLeft:2}}>Searching...</div>}
      {showSuggestions && suggestions.length > 0 && (
        <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:300,background:'var(--surface)',border:'1.5px solid var(--forest-border)',borderRadius:10,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',overflow:'hidden',marginTop:4}}>
          {suggestions.map((f,i) => (
            <div key={i} onMouseDown={() => selectSuggestion(f)} style={{padding:'10px 14px',borderBottom:'1px solid var(--border)',cursor:'pointer',fontSize:12,lineHeight:1.4}} onMouseEnter={e=>(e.currentTarget.style.background='var(--forest-subtle)')} onMouseLeave={e=>(e.currentTarget.style.background='')}>
              <div style={{fontWeight:500}}>{f.properties.formatted}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── MFA ──────────────────────────────────────────────────────────────────────
function MFASetup({ onComplete, onSignOut }) {
  const [qrCode,setQrCode]=useState(''); const [secret,setSecret]=useState(''); const [factorId,setFactorId]=useState(''); const [verifyCode,setVerifyCode]=useState(''); const [error,setError]=useState(''); const [loading,setLoading]=useState(false)
  useEffect(()=>{const enroll=async()=>{const {data,error}=await supabase.auth.mfa.enroll({factorType:'totp',issuer:'Benchmark CRM'}); if(error){setError(error.message);return}; setFactorId(data.id);setQrCode(data.totp.qr_code);setSecret(data.totp.secret)}; enroll()},[])
  const handleVerify=async()=>{ if(verifyCode.length!==6) return setError('Enter the 6-digit code from your app'); setLoading(true);setError(''); const {data:challengeData,error:challengeError}=await supabase.auth.mfa.challenge({factorId}); if(challengeError){setError(challengeError.message);setLoading(false);return}; const {error:verifyError}=await supabase.auth.mfa.verify({factorId,challengeId:challengeData.id,code:verifyCode}); if(verifyError){setError('Invalid code — try again');setLoading(false);return}; setLoading(false);onComplete() }
  return (
    <div className="mfa-wrap"><div className="mfa-card">
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:24}}><div className="logo-box" style={{width:36,height:36,fontSize:16}}>B</div><div><div style={{fontWeight:600,fontSize:14}}>Benchmark CRM</div><div style={{fontSize:11,color:'var(--muted)'}}>Security setup required</div></div></div>
      <div className="mfa-title">Set up two-factor authentication</div><div className="mfa-sub">Your account requires MFA. This only takes 2 minutes.</div>
      {error&&<div className="error-msg">{error}</div>}
      <div className="mfa-step"><div className="mfa-step-num">1</div><div className="mfa-step-title">Download an authenticator app</div><div className="mfa-step-sub">Install Google Authenticator or Authy on your phone.</div></div>
      <div className="mfa-step"><div className="mfa-step-num">2</div><div className="mfa-step-title">Scan this QR code</div>{qrCode&&<div className="qr-wrap"><img src={qrCode} alt="MFA QR" style={{width:160,height:160}} /></div>}{secret&&<div style={{fontSize:11,color:'var(--muted)',textAlign:'center',marginTop:8}}>Manual key: <span style={{fontFamily:'monospace',letterSpacing:2}}>{secret}</span></div>}</div>
      <div className="mfa-step"><div className="mfa-step-num">3</div><div className="mfa-step-title">Enter the 6-digit code</div><div className="mfa-step-sub" style={{marginBottom:12}}>Type the code shown in your authenticator app.</div><input className="form-input" type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={verifyCode} onChange={e=>setVerifyCode(e.target.value.replace(/\D/g,''))} style={{letterSpacing:6,fontSize:20,textAlign:'center'}} /></div>
      <button className="btn btn-primary btn-full" style={{marginTop:16}} onClick={handleVerify} disabled={loading||verifyCode.length!==6}>{loading?'Verifying...':'Activate MFA →'}</button>
      <button className="logout-btn" style={{marginTop:12,textAlign:'center',width:'100%',color:'var(--muted)'}} onClick={onSignOut}>Sign out and use a different account</button>
    </div></div>
  )
}

function MFAChallenge({ onComplete, onSignOut }) {
  const [code,setCode]=useState(''); const [error,setError]=useState(''); const [loading,setLoading]=useState(false)
  const handleVerify=async()=>{ if(code.length!==6) return setError('Enter the 6-digit code'); setLoading(true);setError(''); const {data:factors}=await supabase.auth.mfa.listFactors(); const totpFactor=factors?.totp?.[0]; if(!totpFactor){setError('No MFA factor found');setLoading(false);return}; const {data:challengeData,error:challengeError}=await supabase.auth.mfa.challenge({factorId:totpFactor.id}); if(challengeError){setError(challengeError.message);setLoading(false);return}; const {error:verifyError}=await supabase.auth.mfa.verify({factorId:totpFactor.id,challengeId:challengeData.id,code}); if(verifyError){setError('Invalid code — try again');setLoading(false);return}; setLoading(false);onComplete() }
  return (
    <div className="mfa-wrap"><div className="mfa-card">
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:32}}><div className="logo-box" style={{width:36,height:36,fontSize:16}}>B</div><div><div style={{fontWeight:600,fontSize:14}}>Benchmark CRM</div><div style={{fontSize:11,color:'var(--muted)'}}>Two-factor authentication</div></div></div>
      <div className="mfa-title">Enter your authentication code</div><div className="mfa-sub">Open your authenticator app and enter the 6-digit code for Benchmark CRM.</div>
      {error&&<div className="error-msg">{error}</div>}
      <input className="form-input" type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,''))} style={{letterSpacing:6,fontSize:24,textAlign:'center',marginBottom:16}} autoFocus />
      <button className="btn btn-primary btn-full" onClick={handleVerify} disabled={loading||code.length!==6}>{loading?'Verifying...':'Verify →'}</button>
      <button className="logout-btn" style={{marginTop:12,textAlign:'center',width:'100%',color:'var(--muted)'}} onClick={onSignOut}>Sign out</button>
    </div></div>
  )
}

function ForgotPassword({ onBack }) {
  const [email,setEmail]=useState(''); const [sent,setSent]=useState(false); const [error,setError]=useState(''); const [loading,setLoading]=useState(false)
  const handleSubmit=async(e)=>{ e.preventDefault(); setLoading(true); setError(''); const {error}=await supabase.auth.resetPasswordForEmail(email,{redirectTo:`${window.location.origin}?reset=true`}); if(error){setError(error.message);setLoading(false);return}; setSent(true); setLoading(false) }
  return (
    <div className="login-wrap"><div style={{background:'var(--surface)',borderRadius:20,padding:48,width:420,boxShadow:'0 40px 80px rgba(0,0,0,0.4)'}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:32}}><div className="logo-box" style={{width:36,height:36,fontSize:16}}>B</div><div><div style={{fontWeight:600,fontSize:14}}>Benchmark CRM</div><div style={{fontSize:11,color:'var(--muted)'}}>Password reset</div></div></div>
      {sent?(<div><div style={{fontSize:20,fontWeight:600,marginBottom:8}}>Check your email</div><div style={{color:'var(--muted)',fontSize:13,marginBottom:24,lineHeight:1.6}}>We sent a reset link to <strong>{email}</strong>.</div><button className="btn btn-secondary btn-full" onClick={onBack}>← Back to sign in</button></div>):(
        <><div style={{fontSize:20,fontWeight:600,marginBottom:8}}>Forgot your password?</div><div style={{color:'var(--muted)',fontSize:13,marginBottom:28,lineHeight:1.6}}>Enter your email and we'll send you a reset link.</div>
        {error&&<div className="error-msg">{error}</div>}
        <form onSubmit={handleSubmit}><div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" placeholder="you@benchmarkinsurance.com.au" value={email} onChange={e=>setEmail(e.target.value)} required autoFocus /></div><button type="submit" className="btn btn-primary btn-full" disabled={loading} style={{marginBottom:12}}>{loading?'Sending...':'Send reset link →'}</button></form>
        <button className="btn btn-secondary btn-full" onClick={onBack}>← Back to sign in</button></>
      )}
    </div></div>
  )
}

function ResetPassword({ onComplete }) {
  const [password,setPassword]=useState(''); const [confirm,setConfirm]=useState(''); const [error,setError]=useState(''); const [loading,setLoading]=useState(false); const [done,setDone]=useState(false)
  const handleSubmit=async(e)=>{ e.preventDefault(); if(password.length<12) return setError('Password must be at least 12 characters'); if(password!==confirm) return setError('Passwords do not match'); setLoading(true);setError(''); const {error}=await supabase.auth.updateUser({password}); if(error){setError(error.message);setLoading(false);return}; setDone(true);setLoading(false);setTimeout(()=>onComplete(),2000) }
  return (
    <div className="login-wrap"><div style={{background:'var(--surface)',borderRadius:20,padding:48,width:420,boxShadow:'0 40px 80px rgba(0,0,0,0.4)'}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:32}}><div className="logo-box" style={{width:36,height:36,fontSize:16}}>B</div><div><div style={{fontWeight:600,fontSize:14}}>Benchmark CRM</div><div style={{fontSize:11,color:'var(--muted)'}}>Set new password</div></div></div>
      {done?(<div style={{textAlign:'center',padding:'20px 0'}}><div style={{fontSize:40,marginBottom:16}}>✅</div><div style={{fontSize:18,fontWeight:600,marginBottom:8}}>Password updated</div><div style={{color:'var(--muted)',fontSize:13}}>Redirecting...</div></div>):(
        <><div style={{fontSize:20,fontWeight:600,marginBottom:8}}>Set a new password</div>{error&&<div className="error-msg">{error}</div>}<form onSubmit={handleSubmit}><div className="form-group"><label className="form-label">New password</label><input className="form-input" type="password" placeholder="••••••••••••" value={password} onChange={e=>setPassword(e.target.value)} required autoFocus /></div><div className="form-group"><label className="form-label">Confirm new password</label><input className="form-input" type="password" placeholder="••••••••••••" value={confirm} onChange={e=>setConfirm(e.target.value)} required /></div><button type="submit" className="btn btn-primary btn-full" disabled={loading}>{loading?'Updating...':'Update password →'}</button></form></>
      )}
    </div></div>
  )
}

// ─── PAGE COMPONENTS ──────────────────────────────────────────────────────────
function Clients({ clients, coverConfirmations, openClient, fmtDate, partners }) {
const [search,setSearch]=useState('')
  const [expanded,setExpanded]=useState(new Set())
  const toggleExpand=(id,e)=>{e.stopPropagation();setExpanded(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n})}
  
  const filtered=clients.filter(c=>c.company_name?.toLowerCase().includes(search.toLowerCase())||c.contact_name?.toLowerCase().includes(search.toLowerCase())||c.phone?.includes(search)||c.policy_type?.toLowerCase().includes(search.toLowerCase())||c.assigned_broker?.toLowerCase().includes(search.toLowerCase()))

const getClientRevenue = (c) => {
    const MMS_SPLIT = 0.0695
    const covers = (coverConfirmations||[]).filter(cc=>cc.client_id===c.id)
    let totalInvoice=0, netRevenue=0, insurer='', referralTotal=0
    covers.forEach(cc => {
      const d = cc.cover_data||{}
      // Use commission_amount + broker_fee if entered on commissions page (same source as commissions page)
      const commAmount = Number((d.commission_amount||'').toString().replace(/[^\d.]/g,''))||0
      const fee = Number((d.broker_fee||'').toString().replace(/[^\d.]/g,''))||0
      const basePrem = Number((d.base_premium||'').toString().replace(/[^\d.]/g,''))||0
      const commRate = Number(d.comm_rate||0)
      // Fall back to calculated if commission_amount not entered
      const grossComm = commAmount > 0 ? commAmount : basePrem * (commRate/100)
      const grossIncome = grossComm + fee
      const arIncome = (grossIncome - (grossIncome * MMS_SPLIT)) * 1.1
      const hasReferral = !!c.referring_partner && !d.skip_referral
      const referralCost = hasReferral ? (grossComm * (1 - MCLARDY_COMM_RETENTION)) * getReferralRate(c.referring_partner, partners) : 0
      referralTotal += referralCost
      netRevenue += arIncome - referralCost
      totalInvoice += Number((d.total_invoice||'').toString().replace(/[^\d.]/g,''))||0
      if (d.insurer && !insurer) insurer = d.insurer
    })
    return { totalInvoice, netRevenue, insurer, coverCount:covers.length, referralCost: referralTotal }
  }

  const getDaysUntil = (d) => { if(!d) return null; return Math.ceil((new Date(d)-new Date())/86400000) }
  const getLastContactDays = (c) => {
    const events = (c.audit_log||[]).filter(e=>['note','sms','call','email','contacted'].includes(e.type)).map(e=>e.timestamp).filter(Boolean).map(d=>new Date(d)).filter(d=>!isNaN(d))
    if (!events.length) return null
    return Math.floor((new Date() - new Date(Math.max(...events))) / 86400000)
  }
  const fmtCurr = (n) => { if(!n||n<=0) return '—'; return n.toLocaleString('en-AU',{style:'currency',currency:'AUD',minimumFractionDigits:0,maximumFractionDigits:0}) }

 const totals = filtered.reduce((acc,c) => { const r=getClientRevenue(c); acc.prem+=r.totalInvoice; acc.rev+=r.netRevenue; return acc }, {prem:0,rev:0})

  return (
    <>
      <div className="section-head"><div><div className="section-title">All clients</div><div className="section-sub">{clients.length} clients · {fmtCurr(totals.prem)} premium · {fmtCurr(totals.rev)} net revenue</div></div><input className="form-input" style={{width:260}} placeholder="Search clients..." value={search} onChange={e=>setSearch(e.target.value)} /></div>
      <div className="card">
        <div className="tbl-header" style={{gridTemplateColumns:'2.2fr 0.8fr 1fr 0.9fr 1fr 0.7fr 0.7fr 24px'}}><span>Client</span><span>Broker</span><span>Insurer</span><span>Premium</span><span>Revenue</span><span>Renewal</span><span>Contact</span><span></span></div>
        {filtered.length===0?<div className="empty"><div className="empty-icon">👥</div><div className="empty-title">No clients yet</div><div className="empty-sub">Clients appear here when a lead is moved to Policy Placed</div></div>
        :filtered.map(c=>{
          const rev = getClientRevenue(c)
          const renewDays = getDaysUntil(c.renewal_date)
          const contactDays = getLastContactDays(c)
const isExpanded=expanded.has(c.id)
          const clientCovers=(coverConfirmations||[]).filter(cc=>cc.client_id===c.id)
          const hasMultiple=clientCovers.length>1
          return (
            <div key={c.id}>
            <div className="tbl-row" style={{gridTemplateColumns:'2.2fr 0.8fr 1fr 0.9fr 1fr 0.7fr 0.7fr 24px',borderLeft:'3px solid var(--forest)',background:isExpanded?'var(--forest-subtle)':''}} onClick={()=>openClient(c)}>
              <div className="cell-main">
                <div className="cell-av" style={{background:'var(--forest-subtle)',borderColor:'var(--forest-border)',color:'var(--forest)'}}>{c.company_name?.substring(0,2).toUpperCase()}</div>
                <div><div style={{fontSize:13,fontWeight:600}}>{c.company_name}</div><div style={{display:'flex',gap:4,marginTop:2,flexWrap:'wrap'}}>{c.policy_type&&<span style={{background:'var(--forest-subtle)',color:'var(--forest)',padding:'1px 6px',borderRadius:4,fontSize:10,fontWeight:600}}>{c.policy_type}</span>}{rev.coverCount>1&&<span style={{background:'rgba(59,130,246,0.1)',color:'#2563eb',padding:'1px 6px',borderRadius:4,fontSize:10,fontWeight:600}}>{rev.coverCount} policies</span>}<ClientBadges client={c}/></div></div>
              </div>
              <div style={{fontSize:12,fontWeight:500}}>{c.assigned_broker?.split(' ')[0]||'—'}</div>
              <div style={{fontSize:12,color:'var(--muted)'}}>{rev.insurer||'—'}</div>
              <div style={{fontSize:12,fontWeight:600}}>{fmtCurr(rev.totalInvoice)}</div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:rev.netRevenue>0?'var(--forest)':'var(--muted)'}}>{fmtCurr(rev.netRevenue)}</div>
                {rev.referralCost>0&&<div style={{fontSize:10,color:'var(--orange)'}}>−{fmtCurr(rev.referralCost)} referral</div>}
              </div>
              <div>{renewDays!==null?<span className={`days-pill ${renewDays<=14?'d-red':renewDays<=30?'d-amber':'d-green'}`} style={{fontSize:11}}>{renewDays}d</span>:<span style={{fontSize:12,color:'var(--muted)'}}>—</span>}</div>
<div>{contactDays!==null?<span style={{fontSize:11,fontWeight:600,color:contactDays>14?'var(--danger)':contactDays>7?'#7a5500':'var(--forest)'}}>{contactDays===0?'Today':`${contactDays}d`}</span>:<span style={{fontSize:12,color:'var(--muted)'}}>—</span>}</div>
              <div onClick={hasMultiple?e=>toggleExpand(c.id,e):undefined} style={{display:'flex',alignItems:'center',justifyContent:'center',cursor:hasMultiple?'pointer':'default'}}>{hasMultiple&&<span style={{fontSize:10,color:'var(--muted)',display:'inline-block',transform:isExpanded?'rotate(180deg)':'rotate(0deg)',transition:'transform .2s'}}>▼</span>}</div>
            </div>
            {isExpanded&&clientCovers.map((cc,i)=>{
              const d=cc.cover_data||{}
              const totalInv=Number((d.total_invoice||'').replace(/[^\d.]/g,''))||0
              const policyRenewDays=d.period_to?Math.ceil((new Date(d.period_to)-new Date())/86400000):null
              return (
                <div key={cc.id} style={{display:'grid',gridTemplateColumns:'2.2fr 0.8fr 1fr 0.9fr 1fr 0.7fr 0.7fr 24px',gap:12,padding:'9px 20px',alignItems:'center',background:i%2===0?'rgba(30,61,48,0.03)':'rgba(30,61,48,0.06)',borderBottom:'1px solid var(--border)',borderLeft:'3px solid var(--forest-border)',cursor:'pointer'}} onClick={()=>openClient(c)}>
                  <div style={{display:'flex',alignItems:'center',gap:8,paddingLeft:42}}><div style={{width:5,height:5,borderRadius:'50%',background:'var(--forest)',flexShrink:0}}/><div><div style={{fontSize:12,fontWeight:600}}>{d.cover_type||'Policy'}</div><div style={{fontSize:10,color:'var(--muted)'}}>{d.insurer||'—'}{d.policy_number?` · ${d.policy_number}`:''}</div></div></div>
                  <div style={{fontSize:11,color:'var(--muted)'}}>{cc.created_by?.split(' ')[0]||'—'}</div>
                  <div style={{fontSize:11,color:'var(--muted)'}}>{d.insurer||'—'}</div>
                  <div style={{fontSize:11,fontWeight:600}}>{totalInv>0?fmtCurr(totalInv):'—'}</div>
                  <div style={{fontSize:11,color:'var(--muted)'}}>—</div>
                  <div>{policyRenewDays!==null?<span className={`days-pill ${policyRenewDays<=14?'d-red':policyRenewDays<=30?'d-amber':'d-green'}`} style={{fontSize:10}}>{policyRenewDays}d</span>:<span style={{fontSize:11,color:'var(--muted)'}}>—</span>}</div>
                  <div style={{fontSize:10,color:'var(--muted)'}}>{d.period_from?new Date(d.period_from).toLocaleDateString('en-AU',{day:'numeric',month:'short'}):''}</div>
                  <div/>
                </div>
              )
            })}
            </div>
          )
        })}
      </div>
    </>
  )
}

function Tasks({ tasks, openModal, toggleTask, deleteItem, fmtDate }) {
  const [filter,setFilter]=useState('pending')
  const filtered=tasks.filter(t=>filter==='all'?true:filter==='pending'?!t.completed:t.completed)
  return (
    <>
      <div className="section-head"><div><div className="section-title">Tasks</div><div className="section-sub">{tasks.filter(t=>!t.completed).length} pending</div></div>
        <div style={{display:'flex',gap:8}}>{['pending','completed','all'].map(f=><button key={f} className={`btn btn-sm ${filter===f?'btn-primary':'btn-secondary'}`} onClick={()=>setFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>)}<button className="btn btn-orange btn-sm" onClick={()=>openModal('task')}>+ New task</button></div>
      </div>
      <div className="card">
        {filtered.length===0?<div className="empty"><div className="empty-icon">✅</div><div className="empty-title">No tasks here</div></div>
        :filtered.map(t=>(
          <div key={t.id} className="task-item">
            <div className={`task-check ${t.completed?'done':''}`} onClick={()=>toggleTask(t)}>{t.completed&&<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}</div>
            <div className="task-body" onClick={()=>openModal('task',t)}><div className={`task-text ${t.completed?'done':''}`}>{t.task_text}</div><div className="task-meta">{t.client_name} · {t.assigned_broker}{t.due_date?` · Due ${fmtDate(t.due_date)}`:''}</div></div>
            <div className="priority-dot" style={{background:PRIORITY_COLORS[t.priority]}}/>
        
          </div>
        ))}
      </div>
    </>
  )
}

function Renewals({ renewals, fmtDate, openClient }) {
  const [brokerFilter, setBrokerFilter] = useState('all')
  const [rangeFilter, setRangeFilter] = useState('all')
  const [search, setSearch] = useState('')

  const filtered = renewals.filter(c => {
    if (brokerFilter !== 'all' && c.assigned_broker !== brokerFilter) return false
    if (search && !c.company_name?.toLowerCase().includes(search.toLowerCase()) && !c.policy_type?.toLowerCase().includes(search.toLowerCase())) return false
    if (rangeFilter === 'overdue') return c.days < 0
    if (rangeFilter === '0-30') return c.days >= 0 && c.days <= 30
    if (rangeFilter === '31-60') return c.days >= 31 && c.days <= 60
    if (rangeFilter === '61-90') return c.days >= 61 && c.days <= 90
    return true
  })

  const overdue = renewals.filter(c => c.days < 0).length
  const due30   = renewals.filter(c => c.days >= 0 && c.days <= 30).length
  const due60   = renewals.filter(c => c.days >= 31 && c.days <= 60).length
  const due90   = renewals.filter(c => c.days >= 61 && c.days <= 90).length

  return (
    <>
      <div className="section-head">
        <div>
          <div className="section-title">Renewals Pipeline</div>
          <div className="section-sub">{renewals.length} total · {overdue > 0 ? `${overdue} overdue · ` : ''}{due30} due within 30 days</div>
        </div>
        <input className="form-input" style={{ width: 220 }} placeholder="Search client..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Summary strips */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Overdue', count: overdue, key: 'overdue', color: 'var(--danger)', bg: 'rgba(220,53,69,0.06)', border: 'rgba(220,53,69,0.2)' },
          { label: 'Due 0–30 days', count: due30, key: '0-30', color: 'var(--orange)', bg: 'rgba(242,101,34,0.06)', border: 'rgba(242,101,34,0.2)' },
          { label: 'Due 31–60 days', count: due60, key: '31-60', color: '#e6a817', bg: 'rgba(230,168,23,0.06)', border: 'rgba(230,168,23,0.2)' },
          { label: 'Due 61–90 days', count: due90, key: '61-90', color: 'var(--forest)', bg: 'rgba(30,61,48,0.05)', border: 'var(--forest-border)' },
        ].map(band => (
          <div
            key={band.key}
            onClick={() => setRangeFilter(rangeFilter === band.key ? 'all' : band.key)}
            style={{ background: rangeFilter === band.key ? band.bg : 'var(--surface)', border: `1.5px solid ${rangeFilter === band.key ? band.border : 'var(--border)'}`, borderRadius: 12, padding: '14px 18px', cursor: 'pointer', transition: 'all .15s' }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: band.color, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>{band.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: band.count > 0 ? band.color : 'var(--muted)' }}>{band.count}</div>
          </div>
        ))}
      </div>

      {/* Broker filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {['all', 'Ben Rogers', 'Jesse Roach', 'Mitch Speer'].map(b => (
          <button key={b} onClick={() => setBrokerFilter(b)} style={{ padding: '5px 14px', borderRadius: 20, border: `1.5px solid ${brokerFilter === b ? 'var(--forest)' : 'var(--border)'}`, background: brokerFilter === b ? 'var(--forest)' : 'var(--surface)', color: brokerFilter === b ? '#fff' : 'var(--muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', textTransform: b === 'all' ? 'capitalize' : 'none' }}>
            {b === 'all' ? 'All brokers' : b.split(' ')[0]}
          </button>
        ))}
      </div>

      <div className="card">
        {filtered.length === 0
          ? <div className="empty"><div className="empty-icon">📅</div><div className="empty-title">No renewals match</div><div className="empty-sub">Try adjusting your filters</div></div>
          : filtered.map(c => {
              const isOverdue = c.days < 0
              const pill = isOverdue ? 'd-red' : c.days <= 14 ? 'd-red' : c.days <= 30 ? 'd-amber' : 'd-green'
              return (
                <div key={c.id} className="ren-item" style={{ cursor: 'pointer', transition: 'background .12s' }}
                  onClick={() => openClient(c)}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div className="cell-av" style={{ background: isOverdue ? 'rgba(220,53,69,0.1)' : 'var(--forest-subtle)', color: isOverdue ? 'var(--danger)' : 'var(--forest)', borderColor: isOverdue ? 'rgba(220,53,69,0.2)' : 'var(--forest-border)' }}>
                    {c.company_name?.substring(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.company_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, display: 'flex', gap: 8 }}>
                      {c.policy_type && <span>{c.policy_type}</span>}
                      {c.assigned_broker && <span>· {c.assigned_broker.split(' ')[0]}</span>}
                      {c.phone && <span>· {c.phone}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span className={`days-pill ${pill}`}>
                      {isOverdue ? `${Math.abs(c.days)}d overdue` : `${c.days}d`}
                    </span>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{fmtDate(c.renewal_date)}</div>
                  </div>
                </div>
              )
            })
        }
      </div>
    </>
  )
}
function Partners({ partners, openModal, deleteItem, showToast, loadAll }) {
  const [partnerFilter, setPartnerFilter] = useState('active')
  const filteredPartners = partnerFilter === 'all' ? partners : partnerFilter === 'ni' ? partners.filter(p => p.status === 'Not Interested') : partners.filter(p => p.status !== 'Not Interested')
  return (
    <>
      <div className="section-head"><div><div className="section-title">Referral partners</div><div className="section-sub">{partners.filter(p=>p.status!=='Not Interested').length} active · {partners.filter(p=>p.status==='Not Interested').length} not interested</div></div><div style={{display:'flex',gap:8}}><button className={`btn btn-sm ${partnerFilter==='active'?'btn-primary':'btn-secondary'}`} onClick={()=>setPartnerFilter('active')}>Active</button><button className={`btn btn-sm ${partnerFilter==='ni'?'btn-primary':'btn-secondary'}`} onClick={()=>setPartnerFilter('ni')}>Not Interested</button><button className={`btn btn-sm ${partnerFilter==='all'?'btn-primary':'btn-secondary'}`} onClick={()=>setPartnerFilter('all')}>All</button><button className="btn btn-orange btn-sm" onClick={()=>openModal('partner')}>+ New partner</button></div></div>
      <div className="card">
        <div className="tbl-header" style={{gridTemplateColumns:'2fr 1.2fr 1fr 1fr 90px 140px 90px'}}><span>Partner</span><span>Type</span><span>Phone</span><span>Email</span><span>Leads</span><span>Referral Link</span><span></span></div>
        {filteredPartners.length===0?<div className="empty"><div className="empty-icon">🤝</div><div className="empty-title">No partners yet</div></div>
        :filteredPartners.map(p=>(
          <div key={p.id} className="tbl-row" style={{gridTemplateColumns:'2fr 1.2fr 1fr 1fr 90px 140px 90px'}}>
            <div className="cell-main" onClick={()=>openModal('partner',p)}><div className="cell-av" style={{background:'var(--orange-light)',color:'var(--orange)',borderColor:'rgba(242,101,34,.2)'}}>{p.partner_name?.substring(0,2).toUpperCase()}</div><div><div style={{fontSize:13,fontWeight:600}}>{p.partner_name}</div><div style={{fontSize:11,color:'var(--muted)'}}>{p.contact_name}</div></div></div>
            <div><span className="badge badge-orange">{p.partner_type}</span></div>
            <div style={{fontSize:13}}>{p.phone||'—'}</div>
            <div style={{fontSize:13}}>{p.email||'—'}</div>
            <div><span className="badge badge-orange">{p.lead_count||0}</span></div>
            <div>{p.referral_slug?<button className="btn btn-secondary btn-sm" onClick={e=>{e.stopPropagation();navigator.clipboard.writeText(p.referral_link||`https://referrals.benchmarkinsurance.com.au?partner=${p.referral_slug||p.slug}`);showToast('🔗 Referral link copied!')}}>🔗 Copy Link</button>:<span style={{fontSize:11,color:'var(--muted)'}}>No link</span>}</div>
            <div style={{display:'flex',gap:4}}><button className="btn btn-secondary btn-sm" style={{fontSize:10,color:'var(--muted)'}} onClick={async()=>{const newStatus=p.status==='Not Interested'?'Active':'Not Interested';await supabase.from('partners').update({status:newStatus}).eq('id',p.id);await loadAll();showToast(newStatus==='Not Interested'?'Marked Not Interested':'Reactivated')}}>{p.status==='Not Interested'?'✓ Reactivate':'🚫 NI'}</button><button className="btn btn-danger btn-sm" onClick={()=><div><button className="btn btn-secondary btn-sm" style={{fontSize:10,color:'var(--muted)'}} onClick={async()=>{const newStatus=p.status==='Not Interested'?'Active':'Not Interested';await supabase.from('partners').update({status:newStatus}).eq('id',p.id);await loadAll();showToast(newStatus==='Not Interested'?'Marked Not Interested':'Reactivated')}}>{p.status==='Not Interested'?'✓ Reactivate':'🚫 NI'}</button></div>}>✕</button></div>
          </div>
        ))}
      </div>
    </>
  )
}

function Reports({ leads, clients }) {
  const byStatus=leads.reduce((acc,l)=>{acc[l.lead_status]=(acc[l.lead_status]||0)+1;return acc},{})
  const bySource=leads.reduce((acc,l)=>{const s=l.lead_source||'Unknown';acc[s]=(acc[s]||0)+1;return acc},{})
  const maxS=Math.max(...Object.values(byStatus),1)
  const maxSrc=Math.max(...Object.values(bySource),1)
  return (
    <>
      <div className="stats-grid">
        <div className="stat-card sc-orange"><div className="stat-label">Total leads</div><div className="stat-value c-orange">{leads.length}</div></div>
        <div className="stat-card sc-green"><div className="stat-label">Total clients</div><div className="stat-value c-forest">{clients.length}</div></div>
        <div className="stat-card sc-blue"><div className="stat-label">Conversion rate</div><div className="stat-value c-blue">{leads.length>0?Math.round((clients.length/(leads.length+clients.length))*100):0}%</div></div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginTop:20}}>
        <div className="card">
          <div className="card-header"><div className="card-title">Leads by status</div></div>
          {Object.entries(byStatus).length===0?<div className="empty"><div className="empty-sub">No data yet</div></div>
          :Object.entries(byStatus).sort((a,b)=>b[1]-a[1]).map(([status,count])=>(
            <div key={status} className="report-row">
              <div style={{width:10,height:10,borderRadius:'50%',background:STATUS_COLORS[status]||'#6b8278',flexShrink:0}}/>
              <div style={{flex:1,fontSize:13,fontWeight:500}}>{status}</div>
              <div className="report-bar-wrap"><div className="report-bar" style={{width:`${(count/maxS)*100}%`,background:STATUS_COLORS[status]||'var(--forest)'}}/></div>
              <div style={{fontSize:13,fontWeight:600,minWidth:20}}>{count}</div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="card-header"><div className="card-title">Leads by source</div></div>
          {Object.entries(bySource).length===0?<div className="empty"><div className="empty-sub">No data yet</div></div>
          :Object.entries(bySource).sort((a,b)=>b[1]-a[1]).map(([source,count])=>(
            <div key={source} className="report-row">
              <div style={{width:10,height:10,borderRadius:'50%',background:'var(--info)',flexShrink:0}}/>
              <div style={{flex:1,fontSize:13,fontWeight:500}}>{source}</div>
              <div className="report-bar-wrap"><div className="report-bar" style={{width:`${(count/maxSrc)*100}%`,background:'var(--info)'}}/></div>
              <div style={{fontSize:13,fontWeight:600,minWidth:20}}>{count}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
function Commissions({ clients, coverConfirmations, openClient, loadAll, showToast, partners }) {
  const MMS_SPLIT = 0.0695
  const BUSINESS_START = new Date('2026-04-06')

  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [editSaving, setEditSaving] = useState(false)
  const [showInfo, setShowInfo] = useState(null)
  const [tableFilter, setTableFilter] = useState('all')
  const [donutMode, setDonutMode] = useState('revenue') // 'revenue' | 'count'
  const [hoveredSlice, setHoveredSlice] = useState(null)
  const [cancelId, setCancelId] = useState(null)
  const [cancelForm, setCancelForm] = useState({})
  const [cancelSaving, setCancelSaving] = useState(false)

  const parseCurr = (v) => (v||'').replace(/[^\d.]/g,'')
  const fmtC = (n) => { if(!Number.isFinite(n)||n<=0) return ''; return n.toLocaleString('en-AU',{style:'currency',currency:'AUD',minimumFractionDigits:2,maximumFractionDigits:2}) }
  const fmtShort = (n) => { if(!n||n<=0) return '$0'; return n.toLocaleString('en-AU',{style:'currency',currency:'AUD',minimumFractionDigits:0,maximumFractionDigits:0}) }

  const InfoTip = ({id, text}) => (
    <span style={{position:'relative',display:'inline-block'}}>
      <span onClick={(e)=>{e.stopPropagation();setShowInfo(showInfo===id?null:id)}} style={{cursor:'pointer',fontSize:11,color:'var(--muted)',marginLeft:4,opacity:0.5}}>ⓘ</span>
      {showInfo===id && (
        <div onClick={e=>e.stopPropagation()} style={{position:'absolute',top:'100%',left:-10,zIndex:300,background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:10,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',padding:'12px 14px',width:240,marginTop:6}}>
          <div style={{fontSize:12,color:'var(--text)',lineHeight:1.7}}>{text}</div>
          <button onClick={()=>setShowInfo(null)} style={{position:'absolute',top:6,right:8,background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:14}}>✕</button>
        </div>
      )}
    </span>
  )

  const calcPolicy = (d, client) => {
    const basePrem = Number(parseCurr(d.base_premium||''))||0
    const commRate = Number(d.comm_rate||0)
    const brokerFee = Number(parseCurr(d.broker_fee||''))||0
    const totalInvoice = Number(parseCurr(d.total_invoice||''))||0
    const grossComm = basePrem * (commRate / 100)
    const grossIncome = grossComm + brokerFee
    const mmsShare = grossIncome * MMS_SPLIT
    const arIncome = (grossIncome - mmsShare) * 1.1
    const hasReferral = !!client?.referring_partner
    const skipReferral = !!d.skip_referral
    const referralRate = getReferralRate(client?.referring_partner, partners)
    const referralCost = (hasReferral && !skipReferral) ? (grossComm * (1 - MCLARDY_COMM_RETENTION)) * referralRate : 0
    // If McLardy's remittance has been imported and matched to this policy, use the exact
    // Net Amount Payable stamped onto cover_data — it's already net of any referral partner
    // share (McLardy pays referrers directly), so it replaces the estimate rather than
    // having referralCost subtracted from it again.
    const actualNetRevenue = Number(d._actual_net_revenue)
    const isActual = Number.isFinite(actualNetRevenue) && d._actual_net_revenue !== undefined && d._actual_net_revenue !== null
    let netRevenue = isActual ? actualNetRevenue : (arIncome - referralCost)
    const hasData = basePrem > 0 || brokerFee > 0 || isActual

    // Cancellation adjusts net revenue retroactively wherever this policy is counted
    // (weekly scoreboard, all-time totals) — since those are computed live from
    // calcPolicy on every render, not cached. Nothing is deleted; the original
    // figures stay in cover_data for audit purposes.
    const isCancelled = !!d._cancelled
    const cancellationType = d._cancellation_type || '' // 'full_refund' | 'partial_clawback' | 'no_clawback'
    const clawbackAmount = Number(parseCurr(d._clawback_amount||'')) || 0
    if (isCancelled) {
      if (cancellationType === 'full_refund') netRevenue = 0
      else if (cancellationType === 'partial_clawback') netRevenue = netRevenue - clawbackAmount
      // 'no_clawback' — commission retained, netRevenue stays as calculated above
    }

    return {
      basePrem, commRate, grossComm, grossIncome, mmsShare, arIncome, brokerFee, totalInvoice,
      referralCost, referralRate, netRevenue, hasReferral, hasData, skipReferral,
      referralPartner: client?.referring_partner||null, isActual,
      isCancelled, cancellationType, clawbackAmount,
      cancellationDate: d._cancellation_date || null,
      cancellationReason: d._cancellation_reason || '',
    }
  }

  const policyRows = (coverConfirmations||[]).map(cc => {
    const d = cc.cover_data||{}
    const client = clients.find(c=>c.id===cc.client_id)||{}
    const calc = calcPolicy(d, client)
    return { id:cc.id, clientId:cc.client_id, clientName:client.company_name||'Unknown', broker:cc.created_by||'—', insurer:d.insurer||'—', coverType:d.cover_type||'—', date:cc.created_at, coverData:d, isEndorsement:!!d.is_endorsement, ...calc }
  }).sort((a,b)=>new Date(b.date)-new Date(a.date))

  const filteredRows = tableFilter === 'all' ? policyRows
    : tableFilter === 'entered' ? policyRows.filter(r=>r.hasData && !r.isCancelled)
    : tableFilter === 'cancelled' ? policyRows.filter(r=>r.isCancelled)
    : policyRows.filter(r=>!r.hasData)

  const totals = policyRows.reduce((t,r) => {
    t.invoice+=r.totalInvoice; t.grossIncome+=r.grossIncome; t.grossComm+=r.grossComm; t.mms+=r.mmsShare; t.arIncome+=r.arIncome; t.fee+=r.brokerFee; t.referral+=r.referralCost; t.net+=r.netRevenue
    return t
  }, {invoice:0,grossIncome:0,grossComm:0,mms:0,arIncome:0,fee:0,referral:0,net:0})

  const needsData = policyRows.filter(r=>!r.hasData).length
  const avgFee = policyRows.length > 0 ? totals.fee / policyRows.length : 0

  const now = new Date()
  const weeksInBusiness = Math.max(1, Math.ceil((now - BUSINESS_START) / (7*86400000)))
  const startOfThisWeek = new Date(now); startOfThisWeek.setDate(now.getDate() - now.getDay() + 1); startOfThisWeek.setHours(0,0,0,0)
  const thisWeekNet = policyRows.filter(r => r.date && new Date(r.date) >= startOfThisWeek).reduce((s,r) => s + r.netRevenue, 0)
  const startOfLastWeek = new Date(startOfThisWeek); startOfLastWeek.setDate(startOfLastWeek.getDate() - 7)
  const thisWeekRows = policyRows.filter(r => r.date && new Date(r.date) >= startOfThisWeek)
  const lastWeekRows = policyRows.filter(r => r.date && new Date(r.date) >= startOfLastWeek && new Date(r.date) < startOfThisWeek)

  const byBroker = BROKERS.map(b => {
    const rows = policyRows.filter(r=>r.broker===b)
    const net = rows.reduce((s,r)=>s+r.netRevenue,0)
    const fee = rows.reduce((s,r)=>s+r.brokerFee,0)
    const bThisWeek = thisWeekRows.filter(r=>r.broker===b)
    return {
      broker: b, count: rows.length, net, fee,
      avgRev: rows.length>0 ? net/rows.length : 0,
      avgFee: rows.length>0 ? fee/rows.length : 0,
      thisWeek: { net: bThisWeek.reduce((s,r)=>s+r.netRevenue,0), count: bThisWeek.length },
      lastWeek: { net: lastWeekRows.filter(r=>r.broker===b).reduce((s,r)=>s+r.netRevenue,0), count: lastWeekRows.filter(r=>r.broker===b).length },
    }
  })

  // ── Cover type breakdown for donut ──────────────────────────────────────────
  const DONUT_PALETTE = [
    '#1e3d30','#2a5240','#3a6b54','#4d8c6f','#6aab8a',
    '#8ec5a8','#b5dcc8','#e6a817','#f26522','#c2410c',
    '#3b82f6','#8b5cf6','#ec4899','#14b8a6','#f97316',
  ]

  const coverTypeData = (() => {
    const map = {}
    policyRows.forEach(r => {
      const key = r.coverType || 'Other'
      if (!map[key]) map[key] = { coverType: key, count: 0, revenue: 0 }
      map[key].count += 1
      map[key].revenue += r.netRevenue
    })
    return Object.values(map)
      .sort((a,b) => donutMode === 'revenue' ? b.revenue - a.revenue : b.count - a.count)
      .map((item, i) => ({ ...item, color: DONUT_PALETTE[i % DONUT_PALETTE.length] }))
  })()

  const donutTotal = donutMode === 'revenue'
    ? coverTypeData.reduce((s,d) => s + d.revenue, 0)
    : coverTypeData.reduce((s,d) => s + d.count, 0)

  // Build SVG donut path data
  const donutSlices = (() => {
    const R = 80, r = 50, cx = 100, cy = 100
    let startAngle = -Math.PI / 2
    return coverTypeData.map(item => {
      const value = donutMode === 'revenue' ? item.revenue : item.count
      const fraction = donutTotal > 0 ? value / donutTotal : 0
      const angle = fraction * 2 * Math.PI
      const endAngle = startAngle + angle
      const x1 = cx + R * Math.cos(startAngle), y1 = cy + R * Math.sin(startAngle)
      const x2 = cx + R * Math.cos(endAngle), y2 = cy + R * Math.sin(endAngle)
      const x3 = cx + r * Math.cos(endAngle), y3 = cy + r * Math.sin(endAngle)
      const x4 = cx + r * Math.cos(startAngle), y4 = cy + r * Math.sin(startAngle)
      const large = angle > Math.PI ? 1 : 0
      const path = fraction > 0.999
        ? `M ${cx+R} ${cy} A ${R} ${R} 0 1 1 ${cx+R-0.01} ${cy} Z M ${cx+r} ${cy} A ${r} ${r} 0 1 0 ${cx+r-0.01} ${cy} Z`
        : `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${r} ${r} 0 ${large} 0 ${x4} ${y4} Z`
      const midAngle = startAngle + angle / 2
      startAngle = endAngle
      return { ...item, path, fraction, midAngle, value }
    })
  })()

  const editCalc = (editForm.base_premium || editForm.comm_rate || editForm.broker_fee) ? (() => {
    const bp = Number(parseCurr(editForm.base_premium))||0
    const cr = Number(editForm.comm_rate)||0
    const bf = Number(parseCurr(editForm.broker_fee))||0
    const grossComm = bp * (cr/100)
    const grossIncome = grossComm + bf
    const mms = grossIncome * MMS_SPLIT
    const arIncome = grossIncome - mms
    return { grossComm, grossIncome, mms, arIncome }
  })() : null

  const startEdit = (row) => {
    setEditingId(row.id)
    setEditForm({ base_premium:row.basePrem?String(row.basePrem):'', comm_rate:row.commRate?String(row.commRate):'', broker_fee:row.brokerFee?String(row.brokerFee):'' })
  }

  const saveEdit = async (row) => {
    setEditSaving(true)
    const updatedCoverData = { ...row.coverData, base_premium:editForm.base_premium, comm_rate:editForm.comm_rate, broker_fee:editForm.broker_fee }
    await supabase.from('cover_confirmations').update({cover_data:updatedCoverData}).eq('id',row.id)
    setEditingId(null); setEditForm({}); setEditSaving(false)
    if (loadAll) await loadAll()
    if (showToast) showToast('Commission updated')
  }

  const deleteRecord = async (id) => {
    if (!window.confirm('Delete this record?')) return
    await supabase.from('cover_confirmations').delete().eq('id',id)
    if (loadAll) await loadAll()
    if (showToast) showToast('Record deleted')
  }

  const toggleReferral = async (row) => {
    const updated = {...row.coverData, skip_referral: !row.coverData.skip_referral}
    await supabase.from('cover_confirmations').update({cover_data:updated}).eq('id',row.id)
    if (loadAll) await loadAll()
    if (showToast) showToast(updated.skip_referral ? 'Referral fee skipped' : 'Referral fee re-enabled')
  }

  const startCancel = (row) => {
    setCancelId(row.id)
    setCancelForm({
      cancellation_date: row.cancellationDate || new Date().toISOString().split('T')[0],
      cancellation_type: row.cancellationType || 'no_clawback',
      clawback_amount: row.clawbackAmount ? String(row.clawbackAmount) : '',
      cancellation_reason: row.cancellationReason || '',
    })
  }

  const saveCancel = async (row) => {
    setCancelSaving(true)
    const updated = {
      ...row.coverData,
      _cancelled: true,
      _cancellation_date: cancelForm.cancellation_date,
      _cancellation_type: cancelForm.cancellation_type,
      _clawback_amount: cancelForm.cancellation_type === 'partial_clawback' ? cancelForm.clawback_amount : '',
      _cancellation_reason: cancelForm.cancellation_reason,
    }
    await supabase.from('cover_confirmations').update({cover_data:updated}).eq('id',row.id)
    setCancelId(null); setCancelForm({}); setCancelSaving(false)
    if (loadAll) await loadAll()
    if (showToast) showToast('Policy marked as cancelled')
  }

  const reactivatePolicy = async (row) => {
    if (!window.confirm('Reactivate this policy? This removes the cancellation and clawback flags.')) return
    const updated = {...row.coverData}
    delete updated._cancelled
    delete updated._cancellation_date
    delete updated._cancellation_type
    delete updated._clawback_amount
    delete updated._cancellation_reason
    await supabase.from('cover_confirmations').update({cover_data:updated}).eq('id',row.id)
    if (loadAll) await loadAll()
    if (showToast) showToast('Policy reactivated')
  }

  return (
    <div>

      {/* ── HERO ── */}
      <div style={{background:'linear-gradient(135deg,#1e3d30,#2a5240)',borderRadius:16,padding:'28px 32px',marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:'rgba(255,255,255,0.5)',textTransform:'uppercase',letterSpacing:'.8px',marginBottom:6}}>
            Net Revenue <InfoTip id="net" text="Your actual income after McLardy McShane's 6.95% cut and any referral partner payments." />
          </div>
          <div style={{fontSize:48,fontWeight:700,color:'#fff',lineHeight:1,letterSpacing:'-1px'}}>{fmtShort(totals.net)}</div>
          <div style={{fontSize:13,color:'rgba(255,255,255,0.45)',marginTop:10}}>{policyRows.length} policies · {fmtShort(totals.invoice)} premium · Week {weeksInBusiness}</div>
        </div>
        <div style={{display:'flex',gap:24,alignItems:'center'}}>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:10,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'.6px',marginBottom:6}}>Gross Income <InfoTip id="gross" text="Your AR income after MMS deduction, including GST gross-up." /></div>
            <div style={{fontSize:26,fontWeight:700,color:'rgba(255,255,255,0.95)'}}>{fmtShort(totals.arIncome)}</div>
          </div>
          <div style={{width:1,height:44,background:'rgba(255,255,255,0.12)'}} />
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:10,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'.6px',marginBottom:6}}>MMS (6.95%)</div>
            <div style={{fontSize:26,fontWeight:700,color:'#e6a817'}}>-{fmtShort(totals.mms)}</div>
          </div>
          {totals.referral > 0 && <>
            <div style={{width:1,height:44,background:'rgba(255,255,255,0.12)'}} />
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:10,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'.6px',marginBottom:6}}>Referrals</div>
              <div style={{fontSize:26,fontWeight:700,color:'#f26522'}}>-{fmtShort(totals.referral)}</div>
            </div>
          </>}
          <div style={{width:1,height:44,background:'rgba(255,255,255,0.12)'}} />
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:10,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'.6px',marginBottom:6}}>Avg Fee</div>
            <div style={{fontSize:26,fontWeight:700,color:'#4ade80'}}>{fmtShort(avgFee)}</div>
          </div>
          <div style={{width:1,height:44,background:'rgba(255,255,255,0.12)'}} />
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:10,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'.6px',marginBottom:6}}>This Week</div>
            <div style={{fontSize:26,fontWeight:700,color:'rgba(255,255,255,0.95)'}}>{fmtShort(thisWeekNet)}</div>
          </div>
        </div>
      </div>

      {/* ── COVER MIX + SCOREBOARD ROW ── */}
      <div style={{display:'grid',gridTemplateColumns:'420px 1fr',gap:16,marginBottom:20}}>

        {/* Donut */}
        <div style={{background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:14,padding:'20px 24px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700}}>Policy Mix</div>
            <div style={{display:'flex',gap:4}}>
              {['revenue','count'].map(m => (
                <button key={m} onClick={()=>setDonutMode(m)} style={{padding:'4px 12px',borderRadius:20,border:'1.5px solid '+(donutMode===m?'var(--forest)':'var(--border)'),background:donutMode===m?'var(--forest)':'var(--surface2)',color:donutMode===m?'#fff':'var(--muted)',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'var(--font)',textTransform:'capitalize'}}>
                  {m === 'revenue' ? 'By Revenue' : 'By Count'}
                </button>
              ))}
            </div>
          </div>

          {coverTypeData.length === 0 ? (
            <div className="empty"><div className="empty-icon">🥧</div><div className="empty-title">No data yet</div></div>
          ) : (
            <div style={{display:'flex',gap:16,alignItems:'flex-start'}}>
              {/* SVG Donut */}
              <div style={{position:'relative',flexShrink:0}}>
                <svg width={200} height={200} viewBox="0 0 200 200">
                  {donutSlices.map((slice, i) => (
                    <path
                      key={slice.coverType}
                      d={slice.path}
                      fill={slice.color}
                      opacity={hoveredSlice === null || hoveredSlice === slice.coverType ? 1 : 0.3}
                      style={{cursor:'pointer',transition:'opacity .15s'}}
                      onMouseEnter={() => setHoveredSlice(slice.coverType)}
                      onMouseLeave={() => setHoveredSlice(null)}
                    />
                  ))}
                  {/* Centre label */}
                  <text x="100" y="94" textAnchor="middle" style={{fontSize:10,fill:'var(--muted)',fontFamily:'var(--font)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.5px'}}>
                    {hoveredSlice
                      ? donutSlices.find(s=>s.coverType===hoveredSlice)?.coverType?.split(' ').slice(0,2).join(' ')
                      : donutMode === 'revenue' ? 'Net Rev' : 'Policies'}
                  </text>
                  <text x="100" y="114" textAnchor="middle" style={{fontSize:14,fill:'var(--text)',fontFamily:'var(--font)',fontWeight:700}}>
                    {hoveredSlice
                      ? (() => {
                          const s = donutSlices.find(d=>d.coverType===hoveredSlice)
                          return donutMode === 'revenue' ? fmtShort(s?.revenue||0) : `${s?.count||0} pol`
                        })()
                      : donutMode === 'revenue' ? fmtShort(donutTotal) : `${donutTotal} pol`
                    }
                  </text>
                </svg>
              </div>

              {/* Legend */}
              <div style={{flex:1,overflow:'hidden'}}>
                {coverTypeData.map((item, i) => {
                  const pct = donutTotal > 0 ? Math.round((donutMode==='revenue'?item.revenue:item.count)/donutTotal*100) : 0
                  const isHovered = hoveredSlice === item.coverType
                  return (
                    <div
                      key={item.coverType}
                      onMouseEnter={() => setHoveredSlice(item.coverType)}
                      onMouseLeave={() => setHoveredSlice(null)}
                      style={{display:'flex',alignItems:'center',gap:8,padding:'5px 6px',borderRadius:6,marginBottom:2,cursor:'default',background:isHovered?'var(--surface2)':'transparent',transition:'background .1s'}}
                    >
                      <div style={{width:9,height:9,borderRadius:2,background:item.color,flexShrink:0}} />
                      <div style={{flex:1,fontSize:11,fontWeight:isHovered?600:400,color:'var(--text)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{item.coverType}</div>
                      <div style={{fontSize:11,fontWeight:600,color:'var(--muted)',marginLeft:4,whiteSpace:'nowrap'}}>
                        {donutMode === 'revenue' ? fmtShort(item.revenue) : `${item.count}`}
                      </div>
                      <div style={{fontSize:10,color:'var(--muted)',width:28,textAlign:'right'}}>{pct}%</div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Weekly Scoreboard */}
        <div style={{background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:14,padding:'20px 24px',overflow:'auto'}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Weekly Scoreboard</div>

          <div style={{display:'grid',gridTemplateColumns:'110px repeat(3,1fr) 90px',gap:0}}>
            <div style={{padding:'6px 10px'}} />
            {byBroker.map(b => (
              <div key={'h-'+b.broker} style={{padding:'6px 10px',textAlign:'center'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                  {BROKER_PHOTOS[b.broker] && <img src={BROKER_PHOTOS[b.broker]} alt={b.broker} style={{width:26,height:26,borderRadius:'50%',objectFit:'cover',border:'1.5px solid var(--forest-border)'}} />}
                  <div style={{fontSize:12,fontWeight:700}}>{b.broker.split(' ')[0]}</div>
                </div>
              </div>
            ))}
            <div style={{padding:'6px 10px',textAlign:'center'}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--muted)'}}>Team</div>
            </div>
          </div>

          {(() => {
            const weeks = []
            const cursor = new Date(BUSINESS_START)
            cursor.setDate(cursor.getDate() - cursor.getDay() + 1)
            cursor.setHours(0,0,0,0)
            const nowMonday = startOfThisWeek
            while (cursor.getTime() <= nowMonday.getTime()) {
              const weekStart = new Date(cursor)
              const weekEnd = new Date(cursor); weekEnd.setDate(weekEnd.getDate() + 7)
              const isThisWeek = weekStart.getTime() === nowMonday.getTime()
              const label = isThisWeek ? 'This Week' : weekStart.toLocaleDateString('en-AU',{day:'numeric',month:'short'})
              const brokerNets = byBroker.map(b => {
                const net = policyRows.filter(r => r.broker===b.broker && r.date && new Date(r.date)>=weekStart && new Date(r.date)<weekEnd).reduce((s,r)=>s+r.netRevenue,0)
                const count = policyRows.filter(r => r.broker===b.broker && r.date && new Date(r.date)>=weekStart && new Date(r.date)<weekEnd).length
                return { broker:b.broker, net, count }
              })
              const teamTotal = brokerNets.reduce((s,b)=>s+b.net,0)
              weeks.push({ weekStart, label, isThisWeek, brokerNets, teamTotal })
              cursor.setDate(cursor.getDate() + 7)
            }
            return weeks.reverse().map((w, wi) => (
              <div key={wi} style={{display:'grid',gridTemplateColumns:'110px repeat(3,1fr) 90px',gap:0}}>
                <div style={{padding:'8px 10px',background:w.isThisWeek?'var(--forest-subtle)':wi%2===0?'var(--surface2)':'transparent',fontSize:10,fontWeight:700,color:w.isThisWeek?'var(--forest)':'var(--muted)',textTransform:'uppercase',letterSpacing:'.4px',display:'flex',alignItems:'center'}}>
                  {w.label}
                </div>
                {w.brokerNets.map(b => (
                  <div key={b.broker} style={{padding:'8px 10px',background:w.isThisWeek?'var(--forest-subtle)':wi%2===0?'var(--surface2)':'transparent',textAlign:'center'}}>
                    <div style={{fontSize:14,fontWeight:700,color:b.net>0?(w.isThisWeek?'var(--forest)':'var(--text)'):'var(--muted)'}}>{fmtShort(b.net)}</div>
                    <div style={{fontSize:10,color:'var(--muted)',marginTop:1}}>{b.count} pol</div>
                  </div>
                ))}
                <div style={{padding:'8px 10px',background:w.isThisWeek?'var(--forest-subtle)':wi%2===0?'var(--surface2)':'transparent',textAlign:'center'}}>
                  <div style={{fontSize:14,fontWeight:700,color:w.teamTotal>0?'var(--forest)':'var(--muted)'}}>{fmtShort(w.teamTotal)}</div>
                  <div style={{fontSize:10,color:'var(--muted)',marginTop:1}}>{w.brokerNets.reduce((s,b)=>s+b.count,0)} pol</div>
                </div>
              </div>
            ))
          })()}

          {/* All Time */}
          <div style={{display:'grid',gridTemplateColumns:'110px repeat(3,1fr) 90px',gap:0,marginTop:4}}>
            <div style={{padding:'8px 10px',borderTop:'1.5px solid var(--border)',fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.4px',display:'flex',alignItems:'center'}}>All Time</div>
            {byBroker.map(b => (
              <div key={'at-'+b.broker} style={{padding:'8px 10px',borderTop:'1.5px solid var(--border)',textAlign:'center'}}>
                <div style={{fontSize:14,fontWeight:700,color:'var(--forest)'}}>{fmtShort(b.net)}</div>
                <div style={{fontSize:10,color:'var(--muted)',marginTop:1}}>{b.count} pol · avg {fmtShort(b.avgRev)}</div>
              </div>
            ))}
            <div style={{padding:'8px 10px',borderTop:'1.5px solid var(--border)',textAlign:'center'}}>
              <div style={{fontSize:14,fontWeight:700,color:'var(--forest)'}}>{fmtShort(totals.net)}</div>
              <div style={{fontSize:10,color:'var(--muted)',marginTop:1}}>{policyRows.length} pol</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── POLICY TABLE ── */}
      <div style={{background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:14,overflow:'hidden'}}>
        <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{fontSize:13,fontWeight:700}}>All Policies ({policyRows.length})</div>
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            {needsData > 0 && <span style={{fontSize:11,color:'var(--warning)',fontWeight:600,marginRight:8}}>⚠️ {needsData} need data</span>}
            {['all','entered','missing','cancelled'].map(f => (
              <button key={f} onClick={()=>setTableFilter(f)} style={{padding:'4px 12px',borderRadius:20,border:'1.5px solid '+(tableFilter===f?'var(--forest)':'var(--border)'),background:tableFilter===f?'var(--forest)':'var(--surface2)',color:tableFilter===f?'#fff':'var(--muted)',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'var(--font)',textTransform:'capitalize'}}>{f}</button>
            ))}
          </div>
        </div>
        <div className="tbl-header" style={{gridTemplateColumns:'1.8fr 0.8fr 0.8fr 0.8fr 0.9fr 70px'}}>
          <span>Client</span><span>Cover</span><span>Insurer</span><span>Premium</span><span>Revenue</span><span></span>
        </div>
        {filteredRows.length === 0 ? (
          <div className="empty" style={{padding:'32px 24px'}}>
            <div className="empty-icon">💰</div>
            <div className="empty-title">{tableFilter==='missing' ? 'All policies have data entered' : 'No policies yet'}</div>
          </div>
        ) : filteredRows.map(row => (
          cancelId === row.id ? (
            <div key={row.id} style={{padding:'16px 20px',borderBottom:'1.5px solid var(--border)',background:'rgba(220,53,69,0.05)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:700}}>🚫 Cancel policy — {row.clientName}</div>
                <div style={{fontSize:12,color:'var(--muted)'}}>{row.coverType} · {row.insurer}</div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
                <div>
                  <label className="form-label">Cancellation Date</label>
                  <input className="form-input" type="date" value={cancelForm.cancellation_date||''} onChange={e=>setCancelForm(p=>({...p,cancellation_date:e.target.value}))} />
                </div>
                <div>
                  <label className="form-label">Clawback Type</label>
                  <select className="form-select" value={cancelForm.cancellation_type||'no_clawback'} onChange={e=>setCancelForm(p=>({...p,cancellation_type:e.target.value}))}>
                    <option value="no_clawback">No clawback — commission retained</option>
                    <option value="partial_clawback">Partial clawback (fee + part commission)</option>
                    <option value="full_refund">Full refund — cooling off period</option>
                  </select>
                </div>
                {cancelForm.cancellation_type === 'partial_clawback' && (
                  <div>
                    <label className="form-label">Clawback Amount ($) <InfoTip id="cb-amt" text="The exact $ amount being clawed back from your commission/fee — enter the figure McLardy or the insurer has confirmed." /></label>
                    <input className="form-input" value={cancelForm.clawback_amount||''} onChange={e=>setCancelForm(p=>({...p,clawback_amount:parseCurr(e.target.value)}))} onBlur={e=>setCancelForm(p=>({...p,clawback_amount:fmtC(Number(parseCurr(e.target.value)))}))} onFocus={e=>setCancelForm(p=>({...p,clawback_amount:parseCurr(e.target.value)}))} placeholder="e.g. 150.00" />
                  </div>
                )}
                <div style={cancelForm.cancellation_type === 'partial_clawback' ? {} : {gridColumn:'1 / -1'}}>
                  <label className="form-label">Reason / Notes</label>
                  <input className="form-input" value={cancelForm.cancellation_reason||''} onChange={e=>setCancelForm(p=>({...p,cancellation_reason:e.target.value}))} placeholder="e.g. Non-payment, client sold vehicle, cooling off" />
                </div>
              </div>
              <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:12,color:'var(--muted)',lineHeight:1.6}}>
                {cancelForm.cancellation_type==='full_refund' && 'Net revenue for this policy will show as $0 — full refund to client, nothing retained.'}
                {cancelForm.cancellation_type==='partial_clawback' && `Net revenue will be reduced by the clawback amount entered — original was ${fmtC(row.netRevenue)}.`}
                {cancelForm.cancellation_type==='no_clawback' && 'Net revenue is unchanged — commission is retained regardless of cancellation.'}
                {' '}This adjusts historical totals (weekly scoreboard, all-time) retroactively.
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className="btn btn-secondary btn-sm" onClick={()=>{setCancelId(null);setCancelForm({})}}>Cancel</button>
                <button className="btn btn-danger btn-sm" disabled={cancelSaving} onClick={()=>saveCancel(row)}>{cancelSaving ? 'Saving...' : '🚫 Confirm Cancellation'}</button>
              </div>
            </div>
          ) : editingId === row.id ? (
            <div key={row.id} style={{padding:'16px 20px',borderBottom:'1.5px solid var(--border)',background:'var(--forest-subtle)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:700}}>{row.clientName}</div>
                <div style={{fontSize:12,color:'var(--muted)'}}>{row.coverType} · {row.insurer} · Invoice: {fmtC(row.totalInvoice)}</div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:14}}>
                <div>
                  <label className="form-label">Base Premium ($) <InfoTip id="e-bp" text="The insurer's base premium before taxes, levies and fees." /></label>
                  <input className="form-input" value={editForm.base_premium} onChange={e=>setEditForm(p=>({...p,base_premium:parseCurr(e.target.value)}))} onBlur={e=>setEditForm(p=>({...p,base_premium:fmtC(Number(parseCurr(e.target.value)))}))} onFocus={e=>setEditForm(p=>({...p,base_premium:parseCurr(e.target.value)}))} placeholder="e.g. 1059.13" />
                </div>
                <div>
                  <label className="form-label">Commission Rate (%) <InfoTip id="e-cr" text="The % the insurer pays on base premium." /></label>
                  <input className="form-input" type="number" step="0.5" min="0" max="100" value={editForm.comm_rate} onChange={e=>setEditForm(p=>({...p,comm_rate:e.target.value}))} placeholder="e.g. 15" />
                </div>
                <div>
                  <label className="form-label">Your Broker Fee ex GST ($) <InfoTip id="e-bf" text="YOUR fee only (ex GST). Don't include McLardy's $55 transaction fee." /></label>
                  <input className="form-input" value={editForm.broker_fee} onChange={e=>setEditForm(p=>({...p,broker_fee:parseCurr(e.target.value)}))} onBlur={e=>setEditForm(p=>({...p,broker_fee:fmtC(Number(parseCurr(e.target.value)))}))} onFocus={e=>setEditForm(p=>({...p,broker_fee:parseCurr(e.target.value)}))} placeholder="e.g. 250.00" />
                </div>
              </div>
              {editCalc && editCalc.grossIncome > 0 && (
                <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 16px',marginBottom:14,display:'flex',gap:20,alignItems:'center'}}>
                  <div style={{fontSize:12,color:'var(--muted)'}}>Gross: <strong>{fmtC(editCalc.grossIncome)}</strong></div>
                  <div style={{fontSize:12,color:'var(--warning)'}}>MMS: <strong>-{fmtC(editCalc.mms)}</strong></div>
                  <div style={{fontSize:14,fontWeight:700,color:'var(--forest)',marginLeft:'auto'}}>You keep: {fmtC(editCalc.arIncome)}</div>
                </div>
              )}
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className="btn btn-secondary btn-sm" onClick={()=>{setEditingId(null);setEditForm({})}}>Cancel</button>
                <button className="btn btn-primary btn-sm" disabled={editSaving} onClick={()=>saveEdit(row)}>{editSaving ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          ) : (
            <div key={row.id} className="tbl-row" style={{gridTemplateColumns:'1.8fr 0.8fr 0.8fr 0.8fr 0.9fr 70px',borderLeft:row.isCancelled?'3px solid var(--danger)':row.hasData?'3px solid var(--forest)':'3px solid var(--border)',opacity:row.isCancelled?0.65:1}} onClick={()=>{const c=clients.find(cl=>cl.id===row.clientId);if(c) openClient(c)}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                  {row.clientName}
                  {row.isCancelled && <span style={{fontSize:9,fontWeight:700,color:'var(--danger)',background:'rgba(220,53,69,0.1)',padding:'1px 6px',borderRadius:4,letterSpacing:'.3px'}} title={row.cancellationReason || 'Cancelled'}>CANCELLED</span>}
                </div>
                {row.referralPartner && (
                  <div style={{fontSize:10,color:row.skipReferral?'var(--forest)':'var(--orange)',fontWeight:500}}>
                    {row.referralPartner}{row.skipReferral ? ' · ref skipped' : ` · ${Math.round(row.referralRate*100)}% ref`}
                  </div>
                )}
              </div>
              <div style={{fontSize:12,display:'flex',alignItems:'center',gap:4}} onClick={e=>e.stopPropagation()}>
  {editingId===row.id ? null : (
    <select
      className="form-select"
      style={{fontSize:11,padding:'2px 6px',height:26,borderRadius:6,minWidth:120}}
      value={row.coverType}
      onClick={e=>e.stopPropagation()}
      onChange={async(e)=>{
        const updated={...row.coverData,cover_type:e.target.value}
        await supabase.from('cover_confirmations').update({cover_data:updated}).eq('id',row.id)
        if(loadAll) await loadAll()
        if(showToast) showToast('Cover type updated')
      }}
    >
      {['Truck','Commercial Motor','Car','Plant & Equipment','Commercial Property','Landlord','Public Liability','Professional Indemnity','Management Liability','Carriers','Construction','Farm','Boat','Workers Compensation','PAC','Home & Contents','Caravan','Motorbike','Residential Strata','Marine','Cyber','Travel','Business Package','Other'].map(t=><option key={t}>{t}</option>)}
    </select>
  )}
  {row.isEndorsement&&<span style={{background:'rgba(30,61,48,0.1)',color:'var(--forest)',padding:'1px 6px',borderRadius:4,fontSize:9,fontWeight:700}}>ENDO</span>}
</div>
              <div style={{fontSize:12,color:'var(--muted)'}}>{row.insurer}</div>
              <div style={{fontSize:12,fontWeight:500}}>{fmtC(row.totalInvoice)}</div>
              <div>
                {row.hasData ? (
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:'var(--forest)',display:'flex',alignItems:'center',gap:6}}>
                      {fmtC(row.netRevenue)}
                      {row.isActual && <span style={{fontSize:9,fontWeight:700,color:'var(--forest)',background:'rgba(30,61,48,0.1)',padding:'1px 6px',borderRadius:4,letterSpacing:'.3px'}} title="Exact figure from imported McLardy remittance">✓ ACTUAL</span>}
                    </div>
                    {!row.isActual && row.referralCost > 0 && <div style={{fontSize:10,color:'var(--orange)'}}>after -{fmtC(row.referralCost)} ref</div>}
                  </div>
                ) : (
                  <span style={{fontSize:11,color:'var(--warning)',fontWeight:600,cursor:'pointer'}} onClick={(e)=>{e.stopPropagation();startEdit(row)}}>⚠️ Add data</span>
                )}
              </div>
              <div style={{display:'flex',gap:4}} onClick={e=>e.stopPropagation()}>
                {row.referralPartner && (
                  <button className="btn btn-secondary btn-sm" style={{fontSize:10,padding:'3px 8px',color:row.skipReferral?'var(--forest)':'var(--orange)'}} title={row.skipReferral?'Referral skipped':'Click to skip referral'} onClick={()=>toggleReferral(row)}>{row.skipReferral?'🤝':'🚫'}</button>
                )}
                <button className="btn btn-secondary btn-sm" style={{fontSize:10,padding:'3px 8px'}} onClick={()=>startEdit(row)}>✏️</button>
                {row.isCancelled ? (
                  <button className="btn btn-secondary btn-sm" style={{fontSize:10,padding:'3px 8px',color:'var(--forest)'}} title="Reactivate this policy" onClick={()=>reactivatePolicy(row)}>↩ Reactivate</button>
                ) : (
                  <button className="btn btn-danger btn-sm" style={{fontSize:10,padding:'3px 8px'}} title="Mark policy as cancelled" onClick={()=>startCancel(row)}>⛔ Cancel</button>
                )}
                <button className="btn btn-danger btn-sm" style={{fontSize:10,padding:'3px 8px'}} onClick={()=>deleteRecord(row.id)}>✕</button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  )
}
function CPDTracker({ user, showToast }) {
  const [entries,setEntries]=useState([]); const [loading,setLoading]=useState(true); const [showForm,setShowForm]=useState(false); const [saving,setSaving]=useState(false)
  const [form,setForm]=useState({broker_name:user.name,course_name:'',date_completed:'',cpd_points:'',certificate_url:''})
  const CPD_TARGET_HOURS=25; const currentYear=new Date().getFullYear()
  useEffect(()=>{loadEntries()},[])
  const loadEntries=async()=>{ setLoading(true); const {data}=await supabase.from('cpd_entries').select('*').order('date_completed',{ascending:false}); setEntries(data||[]); setLoading(false) }
  const saveEntry=async()=>{
    if(!form.course_name||!form.date_completed||!form.broker_name) return showToast('Course name, date and broker are required')
    setSaving(true)
    const payload={broker_name:form.broker_name,course_name:form.course_name,date_completed:form.date_completed}
    if(form.cpd_points) payload.cpd_points=parseFloat(form.cpd_points)
    if(form.certificate_url?.trim()) payload.certificate_url=form.certificate_url.trim()
    const {error}=await supabase.from('cpd_entries').insert(payload)
    if(error){showToast('Save failed: '+error.message);setSaving(false);return}
    await loadEntries(); setForm({broker_name:user.name,course_name:'',date_completed:'',cpd_points:'',certificate_url:''}); setShowForm(false); showToast('✅ CPD entry logged'); setSaving(false)
  }
  const deleteEntry=async(id)=>{ if(!window.confirm('Delete this CPD entry?')) return; await supabase.from('cpd_entries').delete().eq('id',id); await loadEntries(); showToast('Deleted') }
  const thisYearEntries=entries.filter(e=>e.date_completed?.startsWith(currentYear.toString()))
  const brokerStats=BROKERS.map(broker=>{ const be=thisYearEntries.filter(e=>e.broker_name===broker); const points=be.reduce((sum,e)=>sum+(e.cpd_points||0),0); return {broker,points,count:be.length} })
  return (
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:24}}>
        {brokerStats.map(({broker,points,count})=>{
          const photo=BROKER_PHOTOS[broker]; const pct=Math.min(100,Math.round((points/CPD_TARGET_HOURS)*100))
          return (
            <div key={broker} className="cpd-broker-card">
              <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
                {photo?<img src={photo} alt={broker} style={{width:44,height:44,borderRadius:'50%',objectFit:'cover',border:'2px solid var(--forest-border)'}}/>:<div style={{width:44,height:44,borderRadius:'50%',background:'var(--forest-subtle)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontWeight:700,color:'var(--forest)'}}>{broker.split(' ').map(w=>w[0]).join('')}</div>}
                <div><div style={{fontSize:14,fontWeight:700}}>{broker}</div><div style={{fontSize:11,color:'var(--muted)'}}>{BROKER_TITLES[broker]}</div></div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                <div style={{background:'var(--surface2)',borderRadius:8,padding:'8px 10px',textAlign:'center'}}><div style={{fontSize:18,fontWeight:700,color:'var(--orange)'}}>{points.toFixed(1)}</div><div style={{fontSize:10,color:'var(--muted)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.3px'}}>CPD Points</div></div>
                <div style={{background:'var(--surface2)',borderRadius:8,padding:'8px 10px',textAlign:'center'}}><div style={{fontSize:18,fontWeight:700,color:'var(--info)'}}>{count}</div><div style={{fontSize:10,color:'var(--muted)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.3px'}}>Courses</div></div>
              </div>
              <div style={{fontSize:11,color:'var(--muted)',marginBottom:4,display:'flex',justifyContent:'space-between'}}><span>Progress to {CPD_TARGET_HOURS} point target</span><span style={{fontWeight:600,color:pct>=100?'var(--forest)':pct>=60?'var(--warning)':'var(--muted)'}}>{pct}%</span></div>
              <div className="cpd-progress-bar"><div className="cpd-progress-fill" style={{width:`${pct}%`}}/></div>
            </div>
          )
        })}
      </div>
      <div className="card">
        <div className="card-header"><div><div className="card-title">CPD Log — {currentYear}</div><div style={{fontSize:12,color:'var(--muted)',marginTop:2}}>{thisYearEntries.length} entries this year</div></div><button className="btn btn-orange btn-sm" onClick={()=>setShowForm(s=>!s)}>{showForm?'✕ Cancel':'+ Log Entry'}</button></div>
        {showForm&&(
          <div style={{padding:'20px',borderBottom:'1.5px solid var(--border)',background:'var(--surface2)'}}>
            <div className="form-grid">
              <div><label className="form-label">Broker *</label><select className="form-select" value={form.broker_name} onChange={e=>setForm(p=>({...p,broker_name:e.target.value}))}>{BROKERS.map(b=><option key={b}>{b}</option>)}</select></div>
              <div><label className="form-label">Date Completed *</label><input className="form-input" type="date" value={form.date_completed} onChange={e=>setForm(p=>({...p,date_completed:e.target.value}))} /></div>
              <div className="full"><label className="form-label">Course Name *</label><input className="form-input" value={form.course_name} onChange={e=>setForm(p=>({...p,course_name:e.target.value}))} placeholder="e.g. The Insured's Duty" /></div>
              <div><label className="form-label">CPD Points</label><input className="form-input" type="number" step="0.5" min="0" value={form.cpd_points} onChange={e=>setForm(p=>({...p,cpd_points:e.target.value}))} placeholder="e.g. 1" /></div>
              <div className="full"><label className="form-label">Certificate URL</label><input className="form-input" type="url" value={form.certificate_url} onChange={e=>setForm(p=>({...p,certificate_url:e.target.value}))} placeholder="https://..." /></div>
            </div>
            <div className="form-actions"><button className="btn btn-secondary" onClick={()=>setShowForm(false)}>Cancel</button><button className="btn btn-primary" onClick={saveEntry} disabled={saving}>{saving?'Saving...':'Save entry'}</button></div>
          </div>
        )}
        {loading?<div className="loading"><div className="spinner"/><span>Loading...</span></div>
        :thisYearEntries.length===0?<div className="empty"><div className="empty-icon">🎓</div><div className="empty-title">No CPD entries yet</div></div>
        :(<>
          <div className="cpd-entry-row" style={{background:'var(--surface2)',fontWeight:600,color:'var(--muted)',fontSize:11,textTransform:'uppercase',letterSpacing:'.4px'}}><span>Course</span><span>Broker</span><span>Points</span><span>Date</span><span></span></div>
          {thisYearEntries.map(e=>(
            <div key={e.id} className="cpd-entry-row">
              <div style={{fontSize:13,fontWeight:600}}>{e.course_name}</div>
              <div style={{fontSize:12,fontWeight:500}}>{e.broker_name?.split(' ')[0]}</div>
              <div style={{fontSize:13,fontWeight:600,color:'var(--orange)'}}>{e.cpd_points||'—'}</div>
              <div style={{fontSize:12,color:'var(--muted)'}}>{e.date_completed?new Date(e.date_completed).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'numeric'}):'—'}</div>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                {e.certificate_url&&<a href={e.certificate_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{fontSize:10,padding:'4px 8px'}}>📄</a>}
                <button className="btn btn-danger btn-sm" style={{fontSize:10,padding:'4px 8px'}} onClick={()=>deleteEntry(e.id)}>✕</button>
              </div>
            </div>
          ))}
        </>)}
      </div>
    </>
  )
}

function LoaSubmissions({ showToast, user, onReviewed }) {
  const [submissions,setSubmissions]=useState([]); const [loading,setLoading]=useState(true)
  useEffect(()=>{ load() },[])
  const load=async()=>{ setLoading(true); const {data}=await supabase.from('loa_submissions').select('*').order('submitted_at',{ascending:false}); setSubmissions(data||[]); setLoading(false) }
  const markReviewed=async(id)=>{ await supabase.from('loa_submissions').update({reviewed:true}).eq('id',id); setSubmissions(prev=>prev.map(s=>s.id===id?{...s,reviewed:true}:s)); onReviewed(); showToast('Marked as reviewed') }
const markNotInterested=async(id)=>{ if(!window.confirm('Mark as Not Interested? Record kept for compliance.')) return; await supabase.from('loa_submissions').update({status:'not_interested'}).eq('id',id); setSubmissions(prev=>prev.map(s=>s.id===id?{...s,status:'not_interested'}:s)); showToast('Marked as not interested') }
  const printLoa=(sub)=>{
    const win=window.open('','_blank')
    win.document.write(`<!DOCTYPE html><html><head><title>Letter of Authority — ${sub.company_name}</title><style>
*{box-sizing:border-box}
body{margin:0;font-family:'DM Sans',Arial,sans-serif;color:#1a2e25;background:#fff}
.page{width:210mm;min-height:297mm;margin:0 auto;background:#fff}
.head{background:#184c3a;color:#fff;padding:18px 28px;display:flex;justify-content:space-between;align-items:center}
.head img{height:40px;filter:brightness(0) invert(1)}
.head-date{font-size:12px;color:rgba(255,255,255,0.6)}
.body{padding:32px 36px}
.name-line{font-size:16px;font-weight:700;color:#1a2e25;border-bottom:1px solid #d0ddd7;padding-bottom:10px;margin-bottom:20px}
p{font-size:13px;line-height:1.8;margin:0 0 12px;color:#1a2e25}
strong{font-weight:700;color:#1f4134}
.title{font-size:22px;font-weight:800;color:#1e3d30;margin:0 0 18px}
.policy-box{background:#eef4f1;border-left:4px solid #1e3d30;padding:10px 14px;border-radius:0 6px 6px 0;margin:8px 0 14px;font-size:13px;color:#1a2e25;font-style:italic}
.sig-section{display:grid;grid-template-columns:1fr 1fr;gap:28px;margin-top:24px;padding-top:16px;border-top:1px solid #d0ddd7}
.sig-label{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#6b8278;margin-bottom:16px}
.sig-line{border-bottom:1.5px solid #1e3d30;min-height:48px;display:flex;align-items:flex-end;padding-bottom:3px;margin-bottom:6px}
.sig-line img{max-height:44px;max-width:100%;object-fit:contain}
.sig-name{font-size:12px;font-weight:600;color:#1a2e25}
.sig-sub{font-size:11px;color:#6b8278}
.foot{padding:12px 28px;border-top:1px solid #d0ddd7;background:#f3f8f5;font-size:10px;color:#6b8278;line-height:1.7}
.foot strong{color:#1e3d30}
@media print{@page{size:A4;margin:0}.page{width:100%}}
</style></head><body>
<div class="page">
  <div class="head">
    <img src="${LOGO_URL}" alt="Benchmark Insurance Brokers"/>
    <div class="head-date">${new Date(sub.submitted_at||Date.now()).toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'})}</div>
  </div>
  <div class="body">
    <div class="name-line">${sub.full_name||''}</div>
    <p>To whom it may concern,</p>
    <div class="title">AUTHORITY TO REVIEW AND QUOTE</div>
    <p>Effective <strong>${sub.effective_date?new Date(sub.effective_date).toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'}):new Date(sub.submitted_at||Date.now()).toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'})}</strong>, I authorise the current and former insurers of <strong>${sub.company_name||'the company'}</strong> to provide <strong>Benchmark Insurance Brokers</strong> with all information (other than premium information) which they request regarding the following insurances and claims history over the past <strong>5 years</strong> for <strong>${sub.company_name||'the company'}</strong>:</p>
    <div class="policy-box">${sub.policies||'All classes of insurance currently held'}</div>
    <p>This authority is provided to enable <strong>Benchmark Insurance Brokers</strong> to review and quote on our insurance arrangements. This does not necessarily appoint them as our insurance broker.</p>
    <p>Whilst this letter of authority is not on any letterhead, I attest to its authenticity and intent in my capacity as <strong>${sub.position||'Director'}</strong> of the company.</p>
    <p>In the event that the Broker receives any personal information from the Insurer, they are required to handle it in accordance with their Privacy Policy.</p>
    <p>Yours faithfully,</p>
    <div class="sig-section">
      <div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${sub.signature?`<img src="${sub.signature}" alt="Signature"/>`:'&nbsp;'}</div>
      </div>
      <div>
        <div class="sig-label">Signatory</div>
        <div class="sig-name">${sub.full_name||'—'}</div>
        <div class="sig-sub">${sub.position||'—'}</div>
        <div class="sig-sub" style="margin-top:8px">${new Date(sub.submitted_at||Date.now()).toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'})}</div>
      </div>
    </div>
  </div>
  <div class="foot">
    <strong>Benchmark Insurance Brokers</strong> is an Authorised Representative of <strong>McLardy McShane Partners Pty Ltd</strong> | AFSL 232987 | ABN 14 064 465 309
  </div>
</div>
</body></html>`)
    win.document.close(); win.focus(); setTimeout(()=>{win.print();win.close()},500)
  }
  const active=submissions.filter(s=>s.status!=='not_interested')
const unreviewed=active.filter(s=>!s.reviewed)
const reviewed=active.filter(s=>s.reviewed)
  return (
    <>
      <div className="section-head"><div><div className="section-title">LOA Submissions</div><div className="section-sub">{unreviewed.length} unreviewed · {submissions.length} total</div></div><button className="quick-btn orange" onClick={()=>{navigator.clipboard.writeText('https://loa.benchmarkinsurance.com.au');showToast('📋 LOA link copied!')}}>📋 Copy LOA Link</button></div>
      {loading?<div className="loading"><div className="spinner"/><span>Loading...</span></div>
      :submissions.length===0?<div className="empty"><div className="empty-icon">📝</div><div className="empty-title">No LOA submissions yet</div></div>
      :(<>
        {unreviewed.length>0&&(<div className="card" style={{marginBottom:20}}><div className="card-header"><div className="card-title">🔔 Unreviewed ({unreviewed.length})</div></div>{unreviewed.map(sub=>(<div key={sub.id} style={{padding:'14px 20px',borderBottom:'1.5px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,background:'rgba(242,101,34,0.03)'}}><div style={{display:'flex',alignItems:'center',gap:12}}><div style={{fontSize:22}}>📄</div><div><div style={{fontSize:13,fontWeight:600}}>{sub.full_name} — {sub.position}</div><div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>{sub.company_name}{sub.abn?` · ABN ${sub.abn}`:''}</div><div style={{fontSize:11,color:'var(--muted)'}}>Submitted {new Date(sub.submitted_at).toLocaleString('en-AU',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})} · Ref: {sub.ref}</div></div></div><div style={{display:'flex',gap:8}}><button className="btn btn-secondary btn-sm" onClick={()=>printLoa(sub)}>🖨️ Print PDF</button><button className="btn btn-primary btn-sm" onClick={()=>markReviewed(sub.id)}>✓ Mark Reviewed</button><button className="btn btn-danger btn-sm" onClick={()=>markNotInterested(sub.id)}>✕ Not Interested</button></div></div>))}</div>)}
        {reviewed.length>0&&(<div className="card"><div className="card-header"><div className="card-title">Reviewed ({reviewed.length})</div></div>{reviewed.map(sub=>(<div key={sub.id} style={{padding:'14px 20px',borderBottom:'1.5px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}><div style={{display:'flex',alignItems:'center',gap:12}}><div style={{fontSize:22}}>📄</div><div><div style={{fontSize:13,fontWeight:600}}>{sub.full_name} — {sub.position}</div><div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>{sub.company_name}{sub.abn?` · ABN ${sub.abn}`:''}</div></div></div><button className="btn btn-secondary btn-sm" onClick={()=>printLoa(sub)}>🖨️ Print PDF</button></div>))}</div>)}
      </>)}
    </>
  )
}
function AppointmentSubmissions({ showToast, user, onReviewed }) {
  const [submissions,setSubmissions]=useState([]); const [loading,setLoading]=useState(true)
  useEffect(()=>{ load() },[])
const load=async()=>{
    setLoading(true)
    const {data, error}=await supabase.from('appointments').select('*').order('submitted_at',{ascending:false})
    console.log('Appointments loaded:', data, 'Error:', error)
    setSubmissions(data||[])
    setLoading(false)
  }
const markReviewed=async(id)=>{ await supabase.from('appointments').update({reviewed:true}).eq('id',id); setSubmissions(prev=>prev.map(s=>s.id===id?{...s,reviewed:true}:s)); onReviewed(); showToast('Marked as reviewed') }
const markNotInterested=async(id)=>{
  if(!window.confirm('Mark this appointment as Not Interested? The record will be retained for compliance purposes.')) return
  await supabase.from('appointments').update({status:'not_interested'}).eq('id',id)
  setSubmissions(prev=>prev.map(s=>s.id===id?{...s,status:'not_interested'}:s))
  showToast('Marked as not interested')
}
const printAppt=(sub)=>{
    const scope = sub.scope_type==='all' ? 'all of the' : 'the following'
    const bizName = sub.business_name||sub.company_name||'the company'
    const policyList = sub.policies ? sub.policies.split('\n').filter(l=>l.trim()).map(l=>`<div style="padding:2px 0">• ${l.trim()}</div>`).join('') : 'All classes of insurance currently held'
    const effectiveDate = sub.effective_date ? new Date(sub.effective_date+'T00:00:00').toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'}) : new Date(sub.submitted_at||Date.now()).toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'})
    const submittedDate = new Date(sub.submitted_at||Date.now()).toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'})
    const win=window.open('','_blank')
    win.document.write(`<!DOCTYPE html><html><head><title>Letter of Appointment — ${bizName}</title><style>
*{box-sizing:border-box}
body{margin:0;font-family:'DM Sans',Arial,sans-serif;color:#1a2e25;background:#fff}
.page{width:210mm;min-height:297mm;margin:0 auto;background:#fff}
.head{background:#184c3a;color:#fff;padding:18px 28px;display:flex;justify-content:space-between;align-items:center}
.head img{height:40px;filter:brightness(0) invert(1)}
.head-date{font-size:12px;color:rgba(255,255,255,0.6)}
.body{padding:32px 36px}
p{font-size:13px;line-height:1.8;margin:0 0 12px;color:#1a2e25}
strong{font-weight:700;color:#1f4134}
.title{font-size:26px;font-weight:800;color:#1e3d30;margin:16px 0 18px;letter-spacing:-.3px}
.salutation{font-size:13px;color:#6b8278;margin-bottom:20px}
.section-head{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:#1e3d30;border-bottom:2px solid #c8ddd5;padding-bottom:5px;margin:20px 0 10px}
.policy-box{background:#eef4f1;border-left:4px solid #1e3d30;padding:10px 14px;border-radius:0 6px 6px 0;margin:8px 0 14px;font-size:13px;color:#1a2e25}
ul{margin:6px 0 12px 18px;padding:0}
ul li{font-size:13px;line-height:1.8;margin-bottom:2px}
.sig-section{display:grid;grid-template-columns:1fr 1fr;gap:28px;margin-top:24px;padding-top:18px;border-top:1.5px solid #d0ddd7}
.sig-block label{font-size:10px;color:#6b8278;text-transform:uppercase;letter-spacing:.5px;display:block;margin-bottom:4px;font-weight:600}
.sig-block-title{font-size:10px;font-weight:700;color:#1e3d30;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px;background:#eef4f1;padding:5px 10px;border-radius:4px;text-align:center}
.sig-line{border-bottom:1.5px solid #1e3d30;min-height:48px;display:flex;align-items:flex-end;padding-bottom:3px;margin-bottom:8px}
.sig-line img{max-height:44px;max-width:100%;object-fit:contain}
.sig-meta strong{color:#1a2e25;font-weight:600;display:block;font-size:12px}
.sig-meta span{font-size:11px;color:#6b8278}
.foot{padding:12px 28px;border-top:1px solid #d0ddd7;background:#f3f8f5;font-size:10px;color:#6b8278;line-height:1.7}
.foot strong{color:#1e3d30}
@media print{@page{size:A4;margin:0}.page{width:100%}}
</style></head><body>
<div class="page">
  <div class="head">
    <img src="${LOGO_URL}" alt="Benchmark Insurance Brokers"/>
    <div class="head-date">${submittedDate}</div>
  </div>
  <div class="body">
    <div class="salutation">To whom it may concern,</div>
    <div class="title">LETTER OF APPOINTMENT</div>

    <p>This letter confirms the appointment of <strong>Benchmark Insurance Group Pty Ltd t/as Benchmark Insurance Brokers</strong> as insurance broker and authorised representative for <strong>${bizName}</strong> in relation to their insurance requirements, effective <strong>${effectiveDate}</strong>.</p>

    <p>By signing this Letter of Appointment, <strong>${bizName}</strong> authorises Benchmark Insurance Brokers to act on their behalf in relation to the following:</p>

    <div class="section-head">Scope of Authorisation</div>
    <ul>
      <li>Obtaining insurance quotations and policy terms</li>
      <li>Providing insurance advice and recommendations</li>
      <li>Arranging, renewing, varying and cancelling insurance policies at the client's instruction</li>
      <li>Liaising and negotiating with insurers, underwriters and relevant third parties</li>
      <li>Assisting with policy administration and claims lodgement</li>
      <li>Collecting information necessary to assess the client's insurance needs</li>
      <li>Acting as primary insurance intermediary unless otherwise advised in writing</li>
    </ul>

    <div class="section-head">Policies Under Appointment</div>
    <div class="policy-box">${policyList}</div>

    <div class="section-head">Scope of Appointment</div>
    <p>This appointment applies to all insurance products and services arranged through Benchmark Insurance Brokers unless otherwise agreed in writing. This appointment does not authorise Benchmark to bind insurance cover without the client's instruction, make false or misleading representations, or act outside the scope of our AFSL authorisation.</p>

    <div class="section-head">Client Acknowledgement</div>
    <p>By signing, <strong>${bizName}</strong> acknowledges that Benchmark Insurance Brokers is appointed as their broker, that information provided is true and accurate, and that they authorise Benchmark to communicate with insurers and relevant third parties on their behalf. The client understands Benchmark may receive commissions which will be disclosed in accordance with regulatory requirements.</p>
    <p>This appointment replaces any existing appointment in place between <strong>${bizName}</strong> and any other insurance broker, and remains in effect until revoked in writing.</p>

    <div class="section-head">Privacy</div>
    <p>The client authorises Benchmark to collect, use and disclose their information for insurance purposes in accordance with our Privacy Policy and the Privacy Act 1988 (Cth).</p>

    <p style="margin-top:20px">Yours faithfully,</p>

    <div class="sig-section">
      <div>
        <div class="sig-block-title">Client Signature</div>
        <div class="sig-block">
          <label>Signature</label>
          <div class="sig-line">${sub.signature?`<img src="${sub.signature}" alt="Signature"/>`:'&nbsp;'}</div>
        </div>
        <div class="sig-meta" style="margin-top:6px">
          <strong>${sub.full_name||'—'}</strong>
          <span>${sub.position||'—'}</span>
        </div>
        <div class="sig-meta" style="margin-top:6px">
          <label style="font-size:10px;color:#6b8278;text-transform:uppercase;letter-spacing:.5px;font-weight:600">Date</label>
          <strong>${effectiveDate}</strong>
        </div>
      </div>
      <div>
        <div class="sig-block-title">On Behalf of Benchmark</div>
        <div class="sig-block">
          <label>Broker</label>
          <div class="sig-meta"><strong>${sub.assigned_broker||'Benchmark Insurance Brokers'}</strong></div>
        </div>
        <div class="sig-block" style="margin-top:10px">
          <label>Signature</label>
          <div class="sig-line">&nbsp;</div>
        </div>
        <div class="sig-meta" style="margin-top:6px">
          <label style="font-size:10px;color:#6b8278;text-transform:uppercase;letter-spacing:.5px;font-weight:600">Date</label>
          <strong>${submittedDate}</strong>
        </div>
      </div>
    </div>
  </div>
  <div class="foot">
    <strong>Benchmark Insurance Group Pty Ltd</strong> t/as <strong>Benchmark Insurance Brokers</strong> | ABN 33 694 361 549 | AR No. 001320555<br/>
    Authorised Representative of <strong>McLardy McShane Partners Pty Ltd</strong> | AFSL 232987 | ABN 14 064 465 309
  </div>
</div>
</body></html>`)
    win.document.close(); win.focus(); setTimeout(()=>{win.print();win.close()},500)
  }
   
  const active=submissions.filter(s=>s.status!=='not_interested')
const unreviewed=active.filter(s=>!s.reviewed)
const reviewed=active.filter(s=>s.reviewed)
const notInterested=submissions.filter(s=>s.status==='not_interested')
return (
    <>
      <div className="section-head"><div><div className="section-title">Appointment Submissions</div><div className="section-sub">{unreviewed.length} unreviewed · {submissions.length} total</div></div><button className="quick-btn orange" onClick={()=>{navigator.clipboard.writeText('https://appointments.benchmarkinsurance.com.au');showToast('📋 Appointment link copied!')}}>📋 Copy Appointment Link</button></div>
      {loading?<div className="loading"><div className="spinner"/><span>Loading...</span></div>
      :submissions.length===0?<div className="empty"><div className="empty-icon">📋</div><div className="empty-title">No appointment submissions yet</div><div className="empty-sub">Clients can submit a Letter of Appointment via the portal link</div></div>
      :(<>
        {unreviewed.length>0&&(<div className="card" style={{marginBottom:20}}><div className="card-header"><div className="card-title">🔔 Unreviewed ({unreviewed.length})</div></div>{unreviewed.map(sub=>(<div key={sub.id} style={{padding:'14px 20px',borderBottom:'1.5px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,background:'rgba(242,101,34,0.03)'}}><div style={{display:'flex',alignItems:'center',gap:12}}><div style={{fontSize:22}}>📋</div><div><div style={{fontSize:13,fontWeight:600}}>{sub.full_name} — {sub.position}</div><div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>{sub.business_name}{sub.abn?` · ABN ${sub.abn}`:''}{sub.email?` · ${sub.email}`:''}</div><div style={{fontSize:11,color:'var(--muted)'}}>Submitted {new Date(sub.submitted_at).toLocaleString('en-AU',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})} · Ref: {sub.ref}</div></div></div><div style={{display:'flex',gap:8}}>
  <button className="btn btn-secondary btn-sm" onClick={()=>printAppt(sub)}>🖨️ Print PDF</button>
  <button className="btn btn-primary btn-sm" onClick={()=>markReviewed(sub.id)}>✓ Mark Reviewed</button>
  <button className="btn btn-danger btn-sm" onClick={()=>markNotInterested(sub.id)}>✕ Not Interested</button>
</div></div>))}</div>)}
        {reviewed.length>0&&(<div className="card"><div className="card-header"><div className="card-title">Reviewed ({reviewed.length})</div></div>{reviewed.map(sub=>(<div key={sub.id} style={{padding:'14px 20px',borderBottom:'1.5px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}><div style={{display:'flex',alignItems:'center',gap:12}}><div style={{fontSize:22}}>📋</div><div><div style={{fontSize:13,fontWeight:600}}>{sub.full_name} — {sub.position}</div><div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>{sub.business_name}{sub.abn?` · ABN ${sub.abn}`:''}{sub.email?` · ${sub.email}`:''}</div></div></div><button className="btn btn-secondary btn-sm" onClick={()=>printAppt(sub)}>🖨️ Print PDF</button></div>))}</div>)}

      </>)}
    </>
  )
}
// ─── RETAIL CLIENT TEST ───────────────────────────────────────────────────────
const RETAIL_PRODUCTS = ['Motor vehicle (<2t)','Home building','Home contents','Travel','Personal & domestic property','Sickness & accident','Public liability']
const WHOLESALE_PRODUCTS = ['Truck / commercial motor','Professional indemnity','Commercial property']
const ALL_PRODUCTS = [...RETAIL_PRODUCTS,...WHOLESALE_PRODUCTS]

function RetailClientTest() {
  const [who,setWho]=useState(null)
  const [product,setProduct]=useState(null)

  const isRetailClient = who==='individual'||who==='small'
  const productIsRetail = RETAIL_PRODUCTS.includes(product)
  const productIsWholesale = WHOLESALE_PRODUCTS.includes(product)

  let result = null
  if(who&&product){
    if(!isRetailClient||!productIsRetail) result='wholesale'
    else result='retail'
  }

  return (
    <div className="card" style={{marginTop:20}}>
      <div className="card-header">
        <div>
          <div className="card-title">Retail client test — interactive</div>
          <div style={{fontSize:12,color:'var(--muted)',marginTop:2}}>Both conditions must be true for retail client obligations to apply</div>
        </div>
        {(who||product)&&<button className="btn btn-secondary btn-sm" onClick={()=>{setWho(null);setProduct(null)}}>Reset</button>}
      </div>

      <div style={{padding:'16px 20px',borderBottom:'1.5px solid var(--border)'}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
          {/* Part 1 */}
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10}}>Part 1 — who are they?</div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {[{id:'individual',label:'Individual'},{id:'small',label:'Small business (<20 employees)'},{id:'large',label:'Large business (20+ employees)'}].map(opt=>(
                <button key={opt.id} onClick={()=>setWho(opt.id)} style={{padding:'8px 14px',borderRadius:8,border:'1.5px solid',fontSize:13,cursor:'pointer',textAlign:'left',fontFamily:'var(--font)',transition:'all .15s',
                  background:who===opt.id?(opt.id==='large'?'rgba(139,92,246,0.1)':'var(--forest-subtle)'):'var(--surface2)',
                  borderColor:who===opt.id?(opt.id==='large'?'var(--purple)':'var(--forest)'):'var(--border)',
                  color:who===opt.id?(opt.id==='large'?'var(--purple)':'var(--forest)'):'var(--text)',
                  fontWeight:who===opt.id?600:400
                }}>{opt.label}</button>
              ))}
            </div>
          </div>
          {/* Part 2 */}
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10}}>Part 2 — what are they buying?</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {ALL_PRODUCTS.map(p=>{
                const isRetail=RETAIL_PRODUCTS.includes(p)
                const sel=product===p
                return (
                  <button key={p} onClick={()=>setProduct(p)} style={{padding:'6px 12px',borderRadius:20,border:'1.5px solid',fontSize:12,cursor:'pointer',fontFamily:'var(--font)',transition:'all .15s',
                    background:sel?(isRetail?'var(--forest-subtle)':'rgba(107,130,120,0.12)'):'var(--surface2)',
                    borderColor:sel?(isRetail?'var(--forest)':'var(--muted)'):'var(--border)',
                    color:sel?(isRetail?'var(--forest)':'var(--text)'):'var(--text)',
                    fontWeight:sel?600:400
                  }}>{p}</button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Result */}
      {result&&(
        <div style={{padding:'14px 20px',borderBottom:'1.5px solid var(--border)',background:result==='retail'?'rgba(30,61,48,0.04)':'rgba(107,130,120,0.06)'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div style={{borderRadius:10,padding:'12px 14px',border:'1.5px solid',borderColor:result==='retail'?'var(--forest-border)':'var(--border)',background:result==='retail'?'var(--forest-subtle)':'var(--surface2)',opacity:result==='retail'?1:0.45}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--forest)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
                <span style={{width:8,height:8,borderRadius:'50%',background:'var(--forest)',display:'inline-block'}}/>
                Retail — obligations apply
              </div>
              {['FSG before advice is given','Duty to take reasonable care — misrepresentation','Target market determination (TMD) required','Informed consent for commission before binding','Complaints rights under AFCA must be disclosed'].map(o=>(
                <div key={o} style={{fontSize:12,color:'var(--forest)',padding:'3px 0',display:'flex',gap:6,alignItems:'flex-start'}}><span style={{color:'var(--forest)',marginTop:1,flexShrink:0}}>•</span>{o}</div>
              ))}
            </div>
            <div style={{borderRadius:10,padding:'12px 14px',border:'1.5px solid',borderColor:result==='wholesale'?'var(--border)':'var(--border)',background:result==='wholesale'?'var(--surface2)':'var(--surface)',opacity:result==='wholesale'?1:0.45}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--info)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
                <span style={{width:8,height:8,borderRadius:'50%',background:'var(--info)',display:'inline-block'}}/>
                Wholesale — simplified obligations
              </div>
              {['FSG still required','Duty of disclosure applies','No TMD requirement','No informed consent for commission','No SOA required — verbal rec sufficient'].map(o=>(
                <div key={o} style={{fontSize:12,color:'var(--text)',padding:'3px 0',display:'flex',gap:6,alignItems:'flex-start'}}><span style={{color:'var(--info)',marginTop:1,flexShrink:0}}>•</span>{o}</div>
              ))}
            </div>
          </div>
          <div style={{marginTop:10,padding:'8px 12px',borderRadius:8,background:result==='retail'?'var(--forest)':'rgba(59,130,246,0.1)',display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:14}}>{result==='retail'?'⚠️':'✓'}</span>
            <span style={{fontSize:13,fontWeight:600,color:result==='retail'?'#fff':'var(--info)'}}>
              {result==='retail'
                ?'Retail client — full obligations apply. FSG, TMD, informed consent and AFCA disclosure required.'
                :(productIsWholesale?'Product not on the retail list — wholesale client regardless of client type.':'Large business — wholesale client. Simplified obligations apply.')}
            </span>
          </div>
        </div>
      )}

      {/* Sequence */}
      <div style={{padding:'14px 20px'}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:12}}>Required info sequence — before cover is bound</div>
        {[
          {n:1,title:'Call script — duty disclosures verbally on the call',sub:'Retail: misrep duty & TMD · Wholesale: duty of disclosure · Claims-made: notification notice',all:true},
          {n:2,title:'Send required info — FSG + important information',sub:'Must be sent before advice is given and cover placed',all:true},
          {n:3,title:'Quote pack — formal recommendation',sub:'Includes options, pricing, key exclusions and advice',all:true},
          {n:4,title:'Informed consent for commission',sub:'5%–26.5% commission range — must be confirmed before binding',all:false},
          {n:5,title:'Cover confirmed — bind and send documents',sub:'FSG + important info included again as links in the email',all:true},
        ].map(step=>{
          const isRetailOnly=!step.all
          const dim=result==='wholesale'&&isRetailOnly
          return (
            <div key={step.n} style={{display:'flex',alignItems:'flex-start',gap:12,padding:'10px 0',borderBottom:'1px solid var(--border)',opacity:dim?0.35:1,transition:'opacity .2s'}}>
              <div style={{width:24,height:24,borderRadius:'50%',background:isRetailOnly?'var(--forest)':'var(--surface2)',border:'1.5px solid',borderColor:isRetailOnly?'var(--forest)':'var(--border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:isRetailOnly?'#fff':'var(--muted)',flexShrink:0,marginTop:1}}>{step.n}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>{step.title}</div>
                <div style={{fontSize:11,color:'var(--muted)'}}>{step.sub}</div>
              </div>
              <span style={{fontSize:10,fontWeight:700,padding:'3px 10px',borderRadius:20,whiteSpace:'nowrap',alignSelf:'center',
                background:isRetailOnly?'var(--forest-subtle)':'var(--surface2)',
                color:isRetailOnly?'var(--forest)':'var(--muted)',
                border:'1px solid',borderColor:isRetailOnly?'var(--forest-border)':'var(--border)'
              }}>{isRetailOnly?'Retail only':'All clients'}</span>
            </div>
          )
        })}
</div>
    </div>
  )
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [session,setSession]=useState(null)
  const [loading,setLoading]=useState(true)
  const [mfaState,setMfaState]=useState('checking')
  const [authScreen,setAuthScreen]=useState('login')
  const navigate = useNavigate()
  const location = useLocation()
  const page = location.pathname.replace(/^\//, '') || 'dashboard'
  const setPage = (p) => navigate('/' + p)

  const [data,setData]=useState({clients:[],tasks:[],partners:[],calls:[],texts:[],coverConfirmations:[]})
  const [dataLoading,setDataLoading]=useState(false)
  const [modal,setModal]=useState(null)
  const [selectedClient,setSelectedClient]=useState(null)
  const [form,setForm]=useState({})
  const [saving,setSaving]=useState(false)
  const [toast,setToast]=useState(null)
  const [error,setError]=useState('')
  const [loginForm,setLoginForm]=useState({email:'',password:''})
  const [factFindModal,setFactFindModal]=useState(false)
const [factFindForm,setFactFindForm]=useState({})
const [factFindSending,setFactFindSending]=useState(false)
  const [loaUnreviewed,setLoaUnreviewed]=useState(0)
  const [apptUnreviewed,setApptUnreviewed]=useState(0)
  const [unreadTexts,setUnreadTexts]=useState(0)
  const seenTextIdsRef = useRef(new Set())

  // ── Auth ───────────────────────────────────────────────────────────────────
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search)
    if(params.get('reset')==='true') setAuthScreen('reset')
    supabase.auth.getSession().then(({data:{session}})=>{ setSession(session); if(session) checkMFA(); else{setMfaState('done');setLoading(false)} })
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_event,session)=>{ setSession(session); if(!session){setMfaState('done');setLoading(false)} })
    return ()=>subscription.unsubscribe()
  },[])

  const checkMFA=async()=>{ try{ const {data:factorsData}=await supabase.auth.mfa.listFactors(); const hasEnrolled=factorsData?.totp?.length>0; if(!hasEnrolled){setMfaState('setup');setLoading(false);return}; const {data:aalData}=await supabase.auth.mfa.getAuthenticatorAssuranceLevel(); setMfaState(aalData?.currentLevel==='aal2'?'done':'challenge') }catch(e){setMfaState('done')}; setLoading(false) }

  useEffect(()=>{ if(session&&mfaState==='done') loadAll() },[session,mfaState])

  // ── Request push permission once logged in ─────────────────────────────────
  useEffect(()=>{ if(session&&mfaState==='done') requestPushPermission() },[session,mfaState])

  // ── Global SMS realtime subscription ──────────────────────────────────────
  useEffect(()=>{
    if(!session||mfaState!=='done') return

    const channel = supabase
      .channel('global-texts')
      .on('postgres_changes',{ event:'INSERT', schema:'public', table:'texts' },(payload)=>{
        const t = payload.new
        if(t.direction !== 'inbound') return

        // Don't notify for texts we already knew about
        if(seenTextIdsRef.current.has(t.id)) return
        seenTextIdsRef.current.add(t.id)

        // Find which client this belongs to
        const clientName = t.client_name || 'Unknown client'
        const preview = (t.body||'').substring(0,100)

        sendPushNotification(
          `💬 New text from ${clientName}`,
          preview,
          ()=>{
            // Find the client and navigate to them
            setData(prev=>{
              const client = prev.clients.find(c=>
                c.id===t.client_id ||
                normaliseAuPhone(c.phone||'')===t.from_number
              )
              if(client){
                setSelectedClient({...client})
                setPage('client')
              }
              return prev
            })
          }
        )

        setUnreadTexts(n=>n+1)
        loadAll()
      })
      .subscribe()

    return ()=>supabase.removeChannel(channel)
  },[session,mfaState])

  // ── Reset unread badge when viewing texts ──────────────────────────────────
  useEffect(()=>{ if(page==='client') setUnreadTexts(0) },[page])

  const loadAll=useCallback(async()=>{
    setDataLoading(true)
    try {
const [clients,tasks,partners,calls,texts,coverConfirmations]=await Promise.all([
        supabase.from('clients').select('*').order('created_at',{ascending:false}),
        supabase.from('tasks').select('*').order('created_at',{ascending:false}),
        supabase.from('partners').select('*').order('created_at',{ascending:false}),
        supabase.from('calls').select('*').order('started_at',{ascending:false}),
        supabase.from('texts').select('*').order('created_at',{ascending:false}),
        supabase.from('cover_confirmations').select('*').eq('status','Sent').order('created_at',{ascending:false}),
      ])
      const clientsData=clients.data||[]

      // Seed seen text IDs so existing texts don't trigger notifications on load
      if(seenTextIdsRef.current.size===0){
        ;(texts.data||[]).forEach(t=>seenTextIdsRef.current.add(t.id))
      }

      const partnersWithCounts=(partners.data||[]).map(partner=>({...partner,lead_count:clientsData.filter(c=>c.referring_partner===partner.partner_name).length}))
      setData({clients:clientsData,tasks:tasks.data||[],partners:partnersWithCounts,calls:calls.data||[],texts:texts.data||[],coverConfirmations:coverConfirmations.data||[]})
const {data:loaData}=await supabase.from('loa_submissions').select('id').eq('reviewed',false);
      setLoaUnreviewed(loaData?.length||0)
      
const {data:apptData}=await supabase.from('appointments').select('id').eq('reviewed',false).neq('status','not_interested');
      setApptUnreviewed(apptData?.length||0);
      
    } catch(e){ showToast('Error loading data') }
    setDataLoading(false)
  },[])

  const handleLogin=async(e)=>{ e?.preventDefault();setError('');setSaving(true); const {data:authData,error}=await supabase.auth.signInWithPassword(loginForm); if(error){setError(error.message);setSaving(false);return}; if(authData.session){setSession(authData.session);await checkMFA()}; setSaving(false) }
  const handleLogout=async()=>{ await supabase.auth.signOut(); setData({clients:[],tasks:[],partners:[],calls:[],texts:[]}); setMfaState('done');setSession(null) }
  const showToast=(msg)=>{ setToast(msg);setTimeout(()=>setToast(null),3500) }


  const openClient=(client)=>{ setSelectedClient({...client});navigate('/client');setModal(null);setUnreadTexts(0) }
  const closeModal=()=>{ setModal(null);setForm({});setSelectedClient(null) }

  const getUserName=()=>{
    const email=session?.user?.email?.toLowerCase()
    if(email==='ben@benchmarkinsurance.com.au') return {name:'Ben Rogers',role:BROKER_TITLES['Ben Rogers']}
    if(email==='jesse@benchmarkinsurance.com.au') return {name:'Jesse Roach',role:BROKER_TITLES['Jesse Roach']}
    if(email==='mitch@benchmarkinsurance.com.au') return {name:'Mitch Speer',role:BROKER_TITLES['Mitch Speer']}
    return {name:email?.split('@')[0]||'Broker',role:'Broker'}
  }

const sendFactFind=async()=>{
  if(!factFindForm.client_name) return showToast('Client name is required')
  if(!factFindForm.client_email) return showToast('Client email is required')
  if(!factFindForm.policy_type) return showToast('Please select a policy type')
  setFactFindSending(true)
  const brokerEmails={'Ben Rogers':'ben@benchmarkinsurance.com.au','Jesse Roach':'jesse@benchmarkinsurance.com.au','Mitch Speer':'mitch@benchmarkinsurance.com.au'}
  const brokerName=factFindForm.assigned_broker||user.name
  const brokerEmail=brokerEmails[brokerName]||'mitch@benchmarkinsurance.com.au'
  const token=Math.random().toString(36).substring(2,10)
  try{
    const {data:{session}}=await supabase.auth.getSession()
    const res=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-fact-find`,{method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${session.access_token}`},body:JSON.stringify({clientName:factFindForm.client_name,clientEmail:factFindForm.client_email,policyType:factFindForm.policy_type,brokerName,brokerEmail,token})})
    const result=await res.json()
    if(!result.success) throw new Error(result.error||'Send failed')
    showToast('📋 Fact find sent to '+factFindForm.client_email)
    setFactFindModal(false)
    setFactFindForm({})
  }catch(err){showToast('⚠️ Failed to send — '+err.message)}
  setFactFindSending(false)
}
  
  const saveNewLead=async()=>{
    if(!form.company_name) return showToast('Client name required')
    setSaving(true)
  const currentUser=getUserName()
    const payload={
      company_name:form.company_name,
      phone:form.phone?normaliseAuPhone(form.phone):null,
      email:form.email||null,
      address:form.address||null,
      referring_partner:form.referring_partner||null,
      sub_referrer:form.sub_referrer||null,
lead_source:form.lead_source||null,
      lead_source_other:form.lead_source_other||null,
      assigned_broker:form.assigned_broker||'Mitch Speer',
      date_of_birth:form.date_of_birth||null,
      lead_status:'New Lead',
     activity_log:[makeActivityEntry('created',`Lead created by ${currentUser.name}`,'🆕')],
     audit_log:[makeAuditEntry('created','Lead created',currentUser.name)]
    }
    const {data:newClient,error}=await supabase.from('clients').insert(payload).select().single()
    if(error){ showToast('⚠️ '+(error.message||'Error saving lead')); setSaving(false); return }

    await loadAll();setSaving(false);closeModal();showToast('Lead created!');setTimeout(()=>openClient(newClient),200)
  }

  const saveTask=async()=>{ if(!form.task_text) return showToast('Task required'); setSaving(true); const payload={task_text:form.task_text,client_name:form.client_name||'General',due_date:form.due_date||null,priority:form.priority||'Medium',assigned_broker:form.assigned_broker||'Mitch Speer',completed:form.completed||false}; const result=form.id?await supabase.from('tasks').update(payload).eq('id',form.id):await supabase.from('tasks').insert(payload); if(result.error){showToast('Error saving task');setSaving(false);return}; await loadAll();closeModal();showToast('Task saved');setSaving(false) }

  const savePartner=async()=>{
    if(!form.partner_name) return showToast('Partner name required')
    setSaving(true)
    const slug=(form.partner_name||'').toLowerCase().trim().replace(/[^a-z0-9\s-]/g,'').replace(/\s+/g,'-').replace(/-+/g,'-')
    const payload={partner_name:form.partner_name,contact_name:form.contact_name,partner_type:form.partner_type||'Asset Finance Broker',phone:form.phone?normaliseAuPhone(form.phone):null,email:form.email,notes:form.notes,slug,referral_slug:slug,referral_link:`https://referrals.benchmarkinsurance.com.au?partner=${slug}`,referral_rate:form.referral_rate?Number(form.referral_rate):40}
    const result=form.id?await supabase.from('partners').update(payload).eq('id',form.id):await supabase.from('partners').insert(payload)
    if(result.error){showToast('Error saving');setSaving(false);return}
    await loadAll();closeModal();showToast(form.id?'Partner updated':`${form.partner_name} added`);setSaving(false)
  }

const exportPolicyTracker = async () => {
  showToast('📊 Preparing export...')
  const { data: covers } = await supabase
    .from('cover_confirmations')
    .select('client_id, cover_data, created_at, created_by')
    .eq('status', 'Sent')
    .order('created_at', { ascending: false })

// Build a client lookup by id
  const clientMap = {}
  placedClients.forEach(c => { clientMap[c.id] = c })

  const fmtD = (d) => {
    if (!d) return ''
    const dt = new Date(d)
    if (isNaN(dt)) return ''
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`
  }

  const fmtComms = (v) => {
    if (!v) return ''
    const n = Number(String(v).replace(/[^\d.]/g,''))
    if (!n) return ''
    return `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

// One row per cover confirmation — correctly handles cross-sells
  const rows = (covers || [])
    .filter(cc => clientMap[cc.client_id]) // only covers with a matched placed client
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map(cc => {
      const c = clientMap[cc.client_id] || {}
      const cover = cc.cover_data || {}
      const dateSold = fmtD(cc.created_at)
      const startDate = fmtD(cover.period_from || c.policy_start_date || cc.created_at)
      const renewalDate = fmtD(cover.period_to || c.renewal_date)
      const advisor = cc.created_by || c.assigned_broker || ''
      const payment = cover.payment_type === 'monthly' ? `Monthly — ${cover.premium_funder || 'Attvest'}` : 'Annual — BPAY'
      const comms = fmtComms(cover.commission_amount)
      const phone = (c.phone || '').replace(/^\+61/, '0').replace(/\s/g, '')
      const coverType = cover.cover_type || c.policy_type || ''
      const insurer = cover.insurer || ''
      const premium = cover.total_invoice ? `$${Number(String(cover.total_invoice).replace(/[^\d.]/g,'')).toLocaleString('en-AU', {minimumFractionDigits:2,maximumFractionDigits:2})}` : ''
      return [dateSold, startDate, renewalDate, c.company_name || '', c.email || '', phone, coverType, insurer, cover.policy_number || '', advisor, payment, premium, comms]
    })

  const headers = ['DATE SOLD', 'POLICY START DATE', 'RENEWAL DATE', 'CLIENT NAME', 'CLIENT EMAIL', 'CLIENT PHONE', 'COVER TYPE', 'INSURER', 'POLICY NUMBER', 'ADVISOR', 'PAYMENT METHOD', 'PREMIUM', 'COMMS']

  // Build HTML for Excel with column widths and formatting baked in
  const colWidths = [14, 16, 14, 28, 32, 16, 22, 18, 18, 14, 22, 14, 14]
  const headerRow = headers.map((h, i) => `<td style="background:#1e3d30;color:#fff;font-weight:bold;font-size:12px;padding:8px 10px;border:1px solid #145230;white-space:nowrap;width:${colWidths[i] * 8}px">${h}</td>`).join('')

  const dataRows = rows.map((row, ri) => {
    const bg = ri % 2 === 0 ? '#ffffff' : '#f0f4f2'
    const cells = row.map((v, ci) => {
      const isDate = ci <= 2
      const isMoney = ci >= 11
      const align = isMoney || isDate ? 'center' : 'left'
      return `<td style="background:${bg};padding:7px 10px;border:1px solid #dde8e2;font-size:12px;text-align:${align};white-space:nowrap;font-family:Arial,sans-serif">${v || ''}</td>`
    }).join('')
    return `<tr>${cells}</tr>`
  }).join('')

  const today = fmtD(new Date())
  const html = `
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Policy Tracker</x:Name><x:WorksheetOptions><x:FreezePanes/><x:FrozenNoSplit/><x:SplitHorizontal>1</x:SplitHorizontal><x:TopRowBottomPane>1</x:TopRowBottomPane></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
<style>
  body { font-family: Arial, sans-serif; }
  table { border-collapse: collapse; }
</style>
</head>
<body>
<table>
  <thead><tr>${headerRow}</tr></thead>
  <tbody>${dataRows}</tbody>
</table>
<p style="font-size:10px;color:#888;margin-top:12px">Benchmark Insurance Brokers — Policy Tracker exported ${today}</p>
</body></html>`

  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `Benchmark_Policy_Tracker_${today.replace(/\//g,'-')}.xls`
  a.click()
  URL.revokeObjectURL(url)
  showToast('✅ Policy tracker exported')
}
const toggleTask=async(task)=>{ await supabase.from('tasks').update({completed:!task.completed}).eq('id',task.id);await loadAll() }
  const deleteItem=async(table,id)=>{ if(!window.confirm('Delete?')) return; await supabase.from(table).delete().eq('id',id);await loadAll();showToast('Deleted') }

  // ── Derived data ───────────────────────────────────────────────────────────
const leads=data.clients.filter(c=>c.lead_status&&c.lead_status!=='Policy Placed')
const placedClients=data.clients.filter(c=>c.lead_status==='Policy Placed'||!c.lead_status||c.is_client)
  const activeLeads=leads.filter(c=>c.lead_status!=='Not Interested')
  const pendingTasks=data.tasks.filter(t=>!t.completed)
  const upcomingRenewals=placedClients.filter(c=>c.renewal_date).map(c=>({...c,days:daysUntil(c.renewal_date)})).filter(c=>c.days<=90).sort((a,b)=>a.days-b.days)

  // Recent activity feed — last 10 texts + calls combined
  const recentActivity = [
    ...data.texts.filter(t=>t.direction==='inbound').slice(0,20).map(t=>({
      id:`text-${t.id}`, type:'text', icon:'💬',
      title:`Text from ${t.client_name||t.from_number||'Unknown'}`,
      body:(t.body||'').substring(0,80),
      timestamp:t.created_at,
      clientId:t.client_id,
      clientName:t.client_name,
    })),
    ...data.calls.slice(0,20).map(c=>({
      id:`call-${c.id}`, type:'call', icon:'📞',
      title:`${c.direction==='inbound'?'Inbound':'Outbound'} call — ${c.client_name||c.contact_number||'Unknown'}`,
      body:c.duration?`${Math.floor(c.duration/60)}m ${c.duration%60}s`:'Missed',
      timestamp:c.started_at,
      clientId:c.client_id,
      clientName:c.client_name,
    })),
  ].sort((a,b)=>new Date(b.timestamp)-new Date(a.timestamp)).slice(0,10)

  const user=getUserName()
const pageTitles={dashboard:'Dashboard',claims:'Claims',meetings:'Meetings',leads:'Leads',clients:'All Clients',renewals:'Renewals',tasks:'Tasks',partners:'Referral Partners',reports:'Reports',commissions:'Commissions',insurerLogins:'Insurer Logins',cpd:'CPD Tracker',complaints:'Complaints',payments:'Payments & Debtors',fso:'FSO Leads',loa:'Letter of Authority',appointments:'Letter of Appointment',search:'Search Results',client:'Client'}

  if(loading) return (<><style>{css}</style><div className="loading"><div className="spinner"/><span>Loading Benchmark CRM...</span></div></>)
  if(authScreen==='reset') return (<><style>{css}</style><ResetPassword onComplete={()=>{setAuthScreen('login');window.history.replaceState({},'','/')}}/></>)
  if(authScreen==='forgot') return (<><style>{css}</style><ForgotPassword onBack={()=>setAuthScreen('login')}/></>)

  if(!session) return (
    <><style>{css}</style>
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-left">
          <img src={LOGO_URL} alt="Benchmark Insurance Brokers" style={{height:64,objectFit:'contain',filter:'brightness(0) invert(1)'}}/>
          <div className="login-hero"><h1>Your clients.<br/>Your pipeline.<br/><span>One place.</span></h1><p>Built exclusively for Benchmark brokers.</p></div>
          <div className="login-stats"><div className="login-stat"><strong>3</strong><span>Brokers</span></div><div className="login-stat"><strong>20+</strong><span>Insurers</span></div><div className="login-stat"><strong>Brisbane</strong><span>Based</span></div></div>
        </div>
        <div className="login-right">
          <div className="login-title">Sign in</div><div className="login-sub">Access your Benchmark CRM</div>
          {error&&<div className="error-msg">{error}</div>}
          <form onSubmit={handleLogin}>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" placeholder="you@benchmarkinsurance.com.au" value={loginForm.email} onChange={e=>setLoginForm(p=>({...p,email:e.target.value}))} required /></div>
            <div className="form-group"><label className="form-label">Password</label><input className="form-input" type="password" placeholder="••••••••" value={loginForm.password} onChange={e=>setLoginForm(p=>({...p,password:e.target.value}))} required /></div>
            <button type="submit" className="btn btn-primary btn-full" disabled={saving}>{saving?'Signing in...':'Sign in →'}</button>
          </form>
          <div style={{textAlign:'center',marginTop:16}}><button onClick={()=>setAuthScreen('forgot')} style={{background:'none',border:'none',color:'var(--muted)',fontSize:13,cursor:'pointer',textDecoration:'underline'}}>Forgot your password?</button></div>
        </div>
      </div>
    </div></>
  )

  if(mfaState==='setup') return (<><style>{css}</style><MFASetup onComplete={()=>{setMfaState('done');loadAll()}} onSignOut={handleLogout}/></>)
  if(mfaState==='challenge') return (<><style>{css}</style><MFAChallenge onComplete={()=>{setMfaState('done');loadAll()}} onSignOut={handleLogout}/></>)

  return (
    <><style>{css}</style>
    <div className="app">
      <div className="sidebar">
        <div className="sidebar-logo"><img src={ICON_URL} alt="Benchmark Insurance Brokers" style={{height:45,objectFit:'contain'}} /></div>
        <nav className="sidebar-nav">
          <div className="nav-section">Overview</div>
          <button className="btn btn-orange btn-sm" style={{width:'100%',justifyContent:'center',margin:'6px 0 10px 0'}} onClick={()=>{setForm({});setModal('newLead')}}>+ New Lead</button>
    <button className="btn btn-secondary btn-sm" style={{width:'100%',justifyContent:'center',margin:'0 0 10px 0'}} onClick={()=>{setFactFindForm({assigned_broker:user.name});setFactFindModal(true)}}>📋 Send Fact Find</button>
          <button className={`nav-item ${page==='dashboard'?'active':''}`} onClick={()=>setPage('dashboard')}><span>⊞</span>Dashboard</button>
          <div className="nav-section">Leads</div>
          <button className={`nav-item ${page==='leads'?'active':''}`} onClick={()=>setPage('leads')}><span>🎯</span>All Leads{activeLeads.length>0&&<span className="nav-badge">{activeLeads.length}</span>}</button>
          <div className="nav-section">Clients</div>
          <button className={`nav-item ${page==='clients'?'active':''}`} onClick={()=>setPage('clients')}><span>👥</span>All Clients</button>
          <button className={`nav-item ${page==='renewals'?'active':''}`} onClick={()=>setPage('renewals')}><span>↻</span>Renewals{upcomingRenewals.filter(c=>c.days<=30).length>0&&<span className="nav-badge nav-badge-warn">{upcomingRenewals.filter(c=>c.days<=30).length}</span>}</button>
          <div className="nav-section">Work</div>
          <button className={`nav-item ${page==='tasks'?'active':''}`} onClick={()=>setPage('tasks')}><span>✓</span>Tasks{pendingTasks.length>0&&<span className="nav-badge">{pendingTasks.length}</span>}</button>
          <button className={`nav-item ${page==='partners'?'active':''}`} onClick={()=>setPage('partners')}><span>🤝</span>Referral Partners</button>
          <div className="nav-section">Insights</div>
          <button className={`nav-item ${page==='reports'?'active':''}`} onClick={()=>setPage('reports')}><span>📊</span>Reports</button>
          <button className={`nav-item ${page==='commissions'?'active':''}`} onClick={()=>setPage('commissions')}><span>💰</span>Commissions</button>
<button className={`nav-item ${page==='payments'?'active':''}`} onClick={()=>setPage('payments')}><span>💳</span>Payments</button>
          <button className={`nav-item ${page==='fso'?'active':''}`} onClick={()=>setPage('fso')}><span>📥</span>FSO Leads</button>
          <div className="nav-section">Compliance</div>
          <button className={`nav-item ${page==='complaints'?'active':''}`} onClick={()=>setPage('complaints')}><span>⚖️</span>Complaints</button>
<button className={`nav-item ${page==='claims'?'active':''}`} onClick={()=>setPage('claims')}><span>🏥</span>Claims</button>
          <button className={`nav-item ${page==='meetings'?'active':''}`} onClick={()=>setPage('meetings')}><span>📋</span>Meetings</button>
          <div className="nav-section">Admin</div>
          
          <button className={`nav-item ${page==='cpd'?'active':''}`} onClick={()=>setPage('cpd')}><span>🎓</span>CPD Tracker</button>
<button className={`nav-item ${page==='loa'?'active':''}`} onClick={()=>setPage('loa')}>
            <span>📝</span>Letter of Authority
            {loaUnreviewed>0&&<span className="nav-badge">{loaUnreviewed}</span>}
          </button>
          <button className={`nav-item ${page==='appointments'?'active':''}`} onClick={()=>setPage('appointments')}>
            <span>📋</span>Letter of Appointment
            {apptUnreviewed>0&&<span className="nav-badge">{apptUnreviewed}</span>}
          </button>
{unreadTexts>0&&(
  <div
    onClick={()=>{
      const latestText=data.texts.filter(t=>t.direction==='inbound').sort((a,b)=>new Date(b.created_at)-new Date(a.created_at))[0]
      if(latestText){
        const client=data.clients.find(c=>c.id===latestText.client_id||normaliseAuPhone(c.phone||'')===latestText.from_number)
        if(client){openClient(client)}
      }
      setUnreadTexts(0)
    }}
    style={{margin:'8px 10px 0',padding:'8px 12px',background:'rgba(242,101,34,0.12)',border:'1px solid rgba(242,101,34,0.3)',borderRadius:8,display:'flex',alignItems:'center',gap:8,cursor:'pointer',transition:'background .15s'}}
    onMouseEnter={e=>e.currentTarget.style.background='rgba(242,101,34,0.22)'}
    onMouseLeave={e=>e.currentTarget.style.background='rgba(242,101,34,0.12)'}
  >
    <span style={{fontSize:14}}>💬</span>
    <span style={{fontSize:12,color:'var(--orange)',fontWeight:600}}>{unreadTexts} new text{unreadTexts!==1?'s':''} →</span>
  </div>
)}
        </nav>
        <div className="sidebar-footer">
          <div className="user-chip">
            {BROKER_PHOTOS[user.name]?<img src={BROKER_PHOTOS[user.name]} alt={user.name} className="broker-avatar"/>:<div className="avatar">{getInitials(user.name)}</div>}
            <div><div className="user-name">{user.name}</div><div className="user-role">{user.role}</div></div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>Sign out</button>
        </div>
      </div>

      <div className="main">
        <div className="topbar">
          <div className="page-title">{pageTitles[page]||'Benchmark CRM'}</div>
          <div className="topbar-actions" style={{display:'flex',gap:8,alignItems:'center'}}>
            <button className="btn btn-secondary btn-sm" style={{borderColor:'rgba(30,61,48,0.4)',color:'var(--forest)',background:'rgba(30,61,48,0.06)',fontWeight:700,whiteSpace:'nowrap'}} onClick={exportPolicyTracker}>📊 Export Tracker</button>
<InstantSearch clients={[...leads,...placedClients]} coverConfirmations={data.coverConfirmations} openClient={openClient} />
          </div>
        </div>

        <div className="content">
          {dataLoading?<div className="loading"><div className="spinner"/><span>Loading...</span></div>:(
            <>
{page==='dashboard'&&(
                <>
                  <Dashboard leads={leads} activeLeads={activeLeads} pendingTasks={pendingTasks} upcomingRenewals={upcomingRenewals} setPage={setPage} openModal={(t,i)=>{setForm(i||{});setModal(t)}} toggleTask={toggleTask} fmtDate={fmtDate} user={user} showToast={showToast} openClient={openClient} coverConfirmations={data.coverConfirmations} clients={[...leads,...placedClients]} texts={data.texts} calls={data.calls}/>
                  <RetailClientTest />
                </>
              )}
              {page==='leads'&&<Leads leads={leads} openClient={openClient} loadAll={loadAll} showToast={showToast} openNewLead={()=>{setForm({});setModal('newLead')}} user={user}/>}
              {page==='clients'&&<Clients clients={placedClients} coverConfirmations={data.coverConfirmations} openClient={openClient} fmtDate={fmtDate} partners={data.partners}/>}
              {page==='tasks'&&<Tasks tasks={data.tasks} openModal={(t,i)=>{setForm(i||{});setModal(t)}} toggleTask={toggleTask} deleteItem={deleteItem} fmtDate={fmtDate}/>}
              {page==='renewals'&&<Renewals renewals={upcomingRenewals} fmtDate={fmtDate} openClient={openClient}/>}
              {page==='partners'&&<Partners partners={data.partners} openModal={(t,i)=>{setForm(i||{});setModal(t)}} deleteItem={deleteItem} showToast={showToast} loadAll={loadAll}/>}
              {page==='reports'&&<Reports leads={leads} clients={placedClients}/>}
              {page==='commissions'&&<Commissions clients={placedClients} coverConfirmations={data.coverConfirmations} openClient={openClient} loadAll={loadAll} showToast={showToast} partners={data.partners}/>}
{page==='payments'&&<Payments showToast={showToast} user={user} clients={[...leads,...placedClients]}/>}
              {page==='fso'&&<FSO clients={[...leads,...placedClients]} coverConfirmations={data.coverConfirmations} openClient={openClient} showToast={showToast} user={user}/>}
              
              {page==='cpd'&&<CPDTracker user={user} showToast={showToast}/>}
            {page==='loa'&&<LoaSubmissions showToast={showToast} user={user} onReviewed={()=>setLoaUnreviewed(0)}/>}
{page==='appointments'&&<AppointmentSubmissions showToast={showToast} user={user} onReviewed={()=>loadAll()}/>}
              {page==='complaints'&&<Complaints showToast={showToast} user={user}/>}
              {page==='claims'&&<Claims showToast={showToast} user={user} clients={[...leads,...placedClients]}/>}
              {page==='meetings'&&<Meetings showToast={showToast} user={user} clients={[...leads,...placedClients]}/>}
              
              {page==='client'&&selectedClient&&<ClientDetailModal client={selectedClient} calls={data.calls} texts={data.texts} loadAll={loadAll} onClose={()=>{setPage('leads');setSelectedClient(null)}} showToast={showToast} fmtDate={fmtDate} user={user}/>}
            </>
          )}
        </div>
      </div>

      {/* NEW LEAD MODAL */}
    {modal==='newLead'&&(
  <div style={{position:'fixed',inset:0,background:'var(--bg)',zIndex:200,overflowY:'auto'}}>
    <div style={{maxWidth:560,margin:'0 auto',padding:'48px 24px'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:32}}>
        <div>
          <div style={{fontSize:22,fontWeight:700,color:'var(--text)'}}>New Lead</div>
          <div style={{fontSize:13,color:'var(--muted)',marginTop:4}}>Fill in the basics — everything else goes in the fact find</div>
        </div>
        <button className="modal-close" onClick={closeModal} style={{width:36,height:36,fontSize:18}}>✕</button>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <div>
          <label className="form-label">Policy Holder Name *</label>
          <input className="form-input" style={{fontSize:15}} value={form.company_name||''} onChange={e=>setForm(p=>({...p,company_name:e.target.value}))} placeholder="e.g. Ben Vassallo or Jones Transport Pty Ltd" autoFocus />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <div>
            <label className="form-label">Phone</label>
            <input className="form-input" value={form.phone||''} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} placeholder="04xx xxx xxx" />
          </div>
          <div>
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email||''} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="email@example.com" />
          </div>
        </div>
        <div>
          <label className="form-label">Assigned Broker</label>
          <select className="form-select" value={form.assigned_broker||'Mitch Speer'} onChange={e=>setForm(p=>({...p,assigned_broker:e.target.value}))}>
            {BROKERS.map(b=><option key={b}>{b}</option>)}
          </select>
        </div>
<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <div>
            <label className="form-label">Lead Source *</label>
            <select className="form-select" value={form.lead_source||''} onChange={e=>setForm(p=>({...p,lead_source:e.target.value,referring_partner:e.target.value==='Referral Partner'?p.referring_partner:'',lead_source_other:e.target.value==='Other'?p.lead_source_other:''}))}>
              <option value="">Select source...</option>
              {LEAD_SOURCES.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            {form.lead_source==='Referral Partner'?(
              <><label className="form-label">Referring Partner *</label>
              <select className="form-select" value={form.referring_partner||''} onChange={e=>setForm(p=>({...p,referring_partner:e.target.value}))}>
                <option value="">Select partner...</option>
                {data.partners.filter(p=>p.status!=='Not Interested').map(p=><option key={p.id} value={p.partner_name}>{p.partner_name}</option>)}
              </select></>
            ):form.lead_source==='Tower Life'?(
              <><label className="form-label">Referred to Tower by <span style={{fontWeight:400,color:'var(--muted)'}}>— optional</span></label>
              <input className="form-input" value={form.sub_referrer||''} onChange={e=>setForm(p=>({...p,sub_referrer:e.target.value}))} placeholder="e.g. Ray White Buderim (who sent it to Tower)" /></>
            ):form.lead_source==='Other'?(
              <><label className="form-label">Specify Source *</label>
              <input className="form-input" value={form.lead_source_other||''} onChange={e=>setForm(p=>({...p,lead_source_other:e.target.value}))} placeholder="e.g. Industry event, cold email response..." /></>
            ):(
              <><label className="form-label">Notes (optional)</label>
              <input className="form-input" value={form.lead_notes||''} onChange={e=>setForm(p=>({...p,lead_notes:e.target.value}))} placeholder="Any extra context..." /></>
            )}
          </div>
        </div>
      </div>
      <div style={{display:'flex',gap:10,marginTop:28}}>
        <button className="btn btn-secondary" style={{flex:1}} onClick={closeModal}>Cancel</button>
        <button className="btn btn-orange" style={{flex:2,fontSize:15,padding:'13px 0'}} onClick={saveNewLead} disabled={saving}>{saving?'Saving...':'Save & start fact find →'}</button>
      </div>
    </div>
  </div>
)}

      {/* TASK MODAL */}
      {modal==='task'&&(
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&closeModal()}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">{form.id?'Edit task':'New task'}</div><button className="modal-close" onClick={closeModal}>✕</button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="full"><label className="form-label">Task *</label><input className="form-input" value={form.task_text||''} onChange={e=>setForm(p=>({...p,task_text:e.target.value}))} placeholder="e.g. Call client with quote" /></div>
                <div><label className="form-label">Client</label><input className="form-input" value={form.client_name||''} onChange={e=>setForm(p=>({...p,client_name:e.target.value}))} /></div>
                <div><label className="form-label">Due date</label><input className="form-input" type="date" value={form.due_date||''} onChange={e=>setForm(p=>({...p,due_date:e.target.value}))} /></div>
                <div><label className="form-label">Priority</label><select className="form-select" value={form.priority||'Medium'} onChange={e=>setForm(p=>({...p,priority:e.target.value}))}><option>High</option><option>Medium</option><option>Low</option></select></div>
                <div><label className="form-label">Assigned to</label><select className="form-select" value={form.assigned_broker||'Mitch Speer'} onChange={e=>setForm(p=>({...p,assigned_broker:e.target.value}))}>{BROKERS.map(b=><option key={b}>{b}</option>)}</select></div>
              </div>
              <div className="form-actions"><button className="btn btn-secondary" onClick={closeModal}>Cancel</button><button className="btn btn-primary" onClick={saveTask} disabled={saving}>{saving?'Saving...':'Save'}</button></div>
            </div>
          </div>
        </div>
      )}

      {/* PARTNER MODAL */}
      {modal==='partner'&&(
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&closeModal()}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">{form.id?'Edit partner':'New referral partner'}</div><button className="modal-close" onClick={closeModal}>✕</button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="full"><label className="form-label">Business name *</label><input className="form-input" value={form.partner_name||''} onChange={e=>setForm(p=>({...p,partner_name:e.target.value}))} /></div>
                <div><label className="form-label">Contact name</label><input className="form-input" value={form.contact_name||''} onChange={e=>setForm(p=>({...p,contact_name:e.target.value}))} /></div>
                <div><label className="form-label">Partner type</label><select className="form-select" value={form.partner_type||'Asset Finance Broker'} onChange={e=>setForm(p=>({...p,partner_type:e.target.value}))}><option>Asset Finance Broker</option><option>Accountant</option><option>Mortgage Broker</option><option>Solicitor</option><option>Truck Dealer</option><option>Equipment Dealer</option><option>Business Network</option><option>Other</option></select></div>
                <div><label className="form-label">Phone</label><input className="form-input" value={form.phone||''} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} /></div>
                <div><label className="form-label">Email</label><input className="form-input" type="email" value={form.email||''} onChange={e=>setForm(p=>({...p,email:e.target.value}))} /></div>
                <div><label className="form-label">Referral rate (%)</label><input className="form-input" type="number" min="0" max="100" step="5" value={form.referral_rate??40} onChange={e=>setForm(p=>({...p,referral_rate:e.target.value}))} placeholder="40" /></div>
                <div className="full"><label className="form-label">Notes</label><textarea className="form-input" value={form.notes||''} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={2} /></div>
              </div>
              <div className="form-actions"><button className="btn btn-secondary" onClick={closeModal}>Cancel</button><button className="btn btn-primary" onClick={savePartner} disabled={saving}>{saving?'Saving...':'Save'}</button></div>
            </div>
          </div>
        </div>
      )}

{factFindModal&&(
  <div className="overlay" onClick={e=>e.target===e.currentTarget&&setFactFindModal(false)}>
    <div className="modal" style={{maxWidth:480}}>
      <div className="modal-head">
        <div className="modal-title">📋 Send Fact Find</div>
        <button className="modal-close" onClick={()=>setFactFindModal(false)}>✕</button>
      </div>
      <div className="modal-body">
        <p style={{fontSize:13,color:'var(--muted)',marginBottom:20,lineHeight:1.6}}>Enter the client's details below. We'll send them a branded email with a link to complete the fact find online. Their answers will be emailed directly to the Benchmark team.y.</p>
        <div className="form-grid" style={{gridTemplateColumns:'1fr'}}>
          <div><label className="form-label">Client Name *</label><input className="form-input" value={factFindForm.client_name||''} onChange={e=>setFactFindForm(p=>({...p,client_name:e.target.value}))} placeholder="e.g. Ben Vassallo or Jones Transport Pty Ltd"/></div>
          <div><label className="form-label">Client Email *</label><input className="form-input" type="email" value={factFindForm.client_email||''} onChange={e=>setFactFindForm(p=>({...p,client_email:e.target.value}))} placeholder="client@email.com.au"/></div>
          <div><label className="form-label">Policy Type *</label><select className="form-select" value={factFindForm.policy_type||''} onChange={e=>setFactFindForm(p=>({...p,policy_type:e.target.value}))}><option value="">Select cover type...</option>{FACT_FIND_OPTIONS.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
          <div><label className="form-label">Assigned Broker</label><select className="form-select" value={factFindForm.assigned_broker||user.name} onChange={e=>setFactFindForm(p=>({...p,assigned_broker:e.target.value}))}>{BROKERS.map(b=><option key={b}>{b}</option>)}</select></div>
        </div>
        {factFindForm.policy_type&&<div style={{marginTop:14,padding:'10px 14px',background:'var(--forest-subtle)',borderRadius:8,border:'1px solid var(--forest-border)',fontSize:12,color:'var(--forest)'}}>📧 Sending to: <strong>{factFindForm.client_email||'client email'}</strong><br/>🔗 <span style={{fontFamily:'monospace',fontSize:11}}>landing.benchmarkinsurance.com.au/fact-find?type={encodeURIComponent(factFindForm.policy_type||'')}</span></div>}
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={()=>setFactFindModal(false)}>Cancel</button>
          <button className="btn btn-orange" onClick={sendFactFind} disabled={factFindSending||!factFindForm.client_email||!factFindForm.policy_type||!factFindForm.client_name}>{factFindSending?'Sending...':'📤 Send Fact Find'}</button>
        </div>
      </div>
    </div>
  </div>
)}

      {toast&&<div className="toast">{toast}</div>}
    </div></>
  )
}
